<template>
  <v-container class="booking-summary-container" fluid v-if="singleBooking && singleBooking.hasOwnProperty('guests')">

    <v-layout row wrap>
      <v-flex lg12>
        <v-container fluid class="pa-2">
          <div class="booking-summary-header">
            <h3 class="knockout-header summary-title">
              BOOKING SUMMARY
              <span v-if="singleBooking && singleBooking.booking_ref_code" class="knockout-header summary-title">
                - REF: {{ singleBooking.booking_ref_code }}
              </span>
            </h3>
            <div class="summary-header-decoration"></div>
          </div>
        </v-container>
      </v-flex>
    </v-layout>

    <v-layout row wrap class="summary-cards-wrapper">
      <v-flex lg3 md4 sm6 xs12
              v-for="(guest, key) in singleBooking.guests"
              v-if="guest.payment_guests > 0"
              :key="key">
        <v-container fluid class="pa-1">
          <v-card class="beautiful-summary-card large-font-card elevation-2">
            <v-container fluid class="pt-3 pb-3">
              <div class="summary-item">
                <span class="summary-item-title text-uppercase">
                  {{ guest.payment_person }}
                </span>
                <span class="count-badge gradient-badge large-badge">
                  {{ guest.payment_guests }}
                </span>
              </div>
              <div class="summary-item-sub">
                {{ guest.guest_type || 'Guest Type' }}
              </div>
              <div class="summary-item-cost">
                {{ guest.payment_person_currency }} {{ $roundOff(guest.payment_person_amount) }} / guest / night
              </div>
              <div class="summary-item-total">
                Total: {{ guest.payment_person_currency }} {{ $roundOff(guest.payment_guests * guest.payment_person_amount * getDays(singleBooking.booking_check_in_date, singleBooking.booking_check_out_date) * ((100 - guest.payment_person_discount) / 100)) }}
              </div>
              <div class="summary-item-nights-guests">
                <span>Nights: {{ getDays(singleBooking.booking_check_in_date, singleBooking.booking_check_out_date) }}</span>
                <span class="ml-2">Guests: {{ guest.payment_guests }}</span>
              </div>

              <div class="verification-status mt-1">
                <template v-if="isGuestVerifiable(guest)">
                  <div class="d-flex align-center flex-wrap mb-1">
                    <v-btn v-if="getGuestVerifiedImages(guest).length > 0"
                           small
                           icon
                           color="primary"
                           @click="viewGuestVerificationImages(guest)"
                           title="View Verification Images">
                      <v-icon small>mdi-image-multiple</v-icon>
                    </v-btn>
                    <v-btn v-else
                           small
                           icon
                           disabled
                           color="grey">
                      <v-icon small>mdi-image-off</v-icon>
                    </v-btn>
                    <span v-if="getGuestVerifiedImages(guest).length > 0" class="ml-1 grey--text caption">
                      ({{ getGuestVerifiedImages(guest).length }})
                    </span>
                  </div>
                  <v-progress-linear :value="(getVerifiedCountForGuest(guest) / guest.payment_guests) * 100"
                                     color="success"
                                     height="6"
                                     class="mb-1"></v-progress-linear>
                  <div class="d-flex align-center flex-wrap">
                    <v-chip :color="getVerifiedCountForGuest(guest) === guest.payment_guests ? 'success' : 'warning'"
                            small
                            text-color="white"
                            class="verification-chip">
                      <v-icon small left>mdi-{{ getVerifiedCountForGuest(guest) === guest.payment_guests ? 'check-circle' : 'alert-circle' }}</v-icon>
                      {{ getVerifiedCountForGuest(guest) }}/{{ guest.payment_guests }} verified
                    </v-chip>
                    <v-chip small outlined class="ml-2" :color="getGuestDocTypeColor(guest)">
                      <v-icon small left>mdi-{{ getGuestDocTypeIcon(guest) }}</v-icon>
                      {{ getGuestDocTypeLabel(guest) }}
                    </v-chip>
                  </div>
                </template>
                <template v-else>
                  <v-chip small color="grey" text-color="white" class="verification-chip">
                    <v-icon small left>mdi-check-circle</v-icon>
                    Exempt
                  </v-chip>
                </template>
              </div>

              <div v-if="guest.proof" class="proof-text mt-1">
                <small class="grey--text">🔑 {{ guest.proof.substring(0, 12) }}...</small>
              </div>
              <div class="summary-item-icon">
                <v-icon small color="#74a533">mdi-account</v-icon>
              </div>
            </v-container>
          </v-card>
        </v-container>
      </v-flex>

      <v-flex lg3 md4 sm6 xs12
              v-for="(vehicle, key) in singleBooking.vehicles"
              v-if="vehicle.vehicles > 0"
              :key="key">
        <v-container fluid class="pa-1">
          <v-card class="beautiful-summary-card large-font-card elevation-2">
            <v-container fluid class="pt-3 pb-3">
              <div class="summary-item">
                <span class="summary-item-title text-uppercase">
                  {{ vehicle.vehicle_charge_category }}
                </span>
                <span class="count-badge gradient-badge large-badge">
                  {{ vehicle.vehicles }}
                </span>
              </div>
              <div class="summary-item-sub">Vehicle Type</div>
              <div class="summary-item-cost">
                {{ singleBooking.currency }} {{ $roundOff(vehicle.vehicle_charge_category_cost) }} / vehicle / night
              </div>
              <div class="summary-item-total">
                Total: {{ singleBooking.currency }} {{ $roundOff(vehicle.vehicles * vehicle.vehicle_charge_category_cost * getDays(singleBooking.booking_check_in_date, singleBooking.booking_check_out_date) * ((100 - vehicle.discount) / 100)) }}
              </div>
              <div class="summary-item-nights-vehicles">
                <span>Nights: {{ getDays(singleBooking.booking_check_in_date, singleBooking.booking_check_out_date) }}</span>
                <span class="ml-2">Vehicles: {{ vehicle.vehicles }}</span>
              </div>
              <div class="summary-item-icon">
                <v-icon small color="#74a533">mdi-car</v-icon>
              </div>
            </v-container>
          </v-card>
        </v-container>
      </v-flex>

      <v-flex lg3 md4 sm6 xs12
              v-for="(facility, key) in singleBooking.facility_bookings"
              :key="key">
        <v-container fluid class="pa-1">
          <v-card class="beautiful-summary-card large-font-card elevation-2">
            <v-container fluid class="pt-3 pb-3">
              <div class="summary-item">
                <span class="summary-item-title text-uppercase clickable" @click="openEditAccommodationDatesDialog(facility)">
                  {{ facility.facility_name }}
                  <span v-if="facility.facility_catering_type_name" class="facility-catering">
                    ({{ facility.facility_catering_type_name }})
                  </span>
                </span>
                <span class="count-badge gradient-badge large-badge">
                  {{ facility.facility_booking_guests }}
                </span>
              </div>
              <div class="summary-item-sub">
                {{ formated_date(facility.facility_booking_check_in_date) }} - {{ formated_date(facility.facility_booking_check_out_date) }}
              </div>
              <div class="summary-item-cost">
                {{ facility.facility_no_of_nights }} night(s)
              </div>
              <div class="summary-item-total">
                Total: {{ facility.facility_booking_currency }} {{ $roundOff(facility.facility_booking_total_cost) }}
              </div>
              <div class="summary-item-nights-guests">
                <span>Nights: {{ facility.facility_no_of_nights }}</span>
                <span class="ml-2">Guests: {{ facility.facility_booking_guests }}</span>
              </div>
              <div class="summary-item-icon">
                <v-icon small color="#74a533">mdi-bed</v-icon>
              </div>
            </v-container>
          </v-card>
        </v-container>
      </v-flex>

      <v-flex lg3 md4 sm6 xs12
              v-for="(facility, key) in singleBooking.external_facility_bookings"
              :key="key">
        <v-container fluid class="pa-1">
          <v-card class="beautiful-summary-card large-font-card elevation-2">
            <v-container fluid class="pt-3 pb-3">
              <div class="summary-item">
                <span class="summary-item-title text-uppercase clickable" @click="openExternalFacilityDetail(facility)">
                  {{ facility.facility_name }}
                </span>
                <span class="count-badge gradient-badge large-badge">
                  {{ facility.pricing_parameter.parameter_name }}
                </span>
              </div>
              <div class="summary-item-sub">
                {{ formated_date(facility.facility_booking_check_in_date) }} - {{ formated_date(facility.facility_booking_check_out_date) }}
              </div>
              <div class="summary-item-cost">
                {{ facility.facility_no_of_nights }} night(s)
              </div>
              <div class="summary-item-total">
                Total: {{ facility.facility_booking_currency }} {{ $roundOff(facility.facility_booking_total_cost) }}
              </div>
              <div class="summary-item-nights-guests">
                <span>Nights: {{ facility.facility_no_of_nights }}</span>
              </div>
              <div class="summary-item-icon">
                <v-icon small color="#74a533">mdi-home-variant</v-icon>
              </div>
            </v-container>
          </v-card>
        </v-container>
      </v-flex>

      <v-flex lg3 md4 sm6 xs12
              v-for="(inventory, key) in singleBooking.inventory_bookings"
              :key="key">
        <v-container fluid class="pa-1">
          <v-card class="beautiful-summary-card large-font-card elevation-2">
            <v-container fluid class="pt-3 pb-3">
              <div class="summary-item">
                <span class="summary-item-title text-uppercase clickable" @click="openEditActivityDateDialog(inventory)">
                  {{ inventory.inventory_name }}
                  <span v-if="inventory.time_frame" class="facility-catering">
                    ({{ inventory.time_frame }})
                  </span>
                </span>
                <span class="count-badge gradient-badge large-badge">
                  {{ inventory.inventory_booking_guests }}
                </span>
              </div>
              <div class="summary-item-sub">
                {{ formated_date(inventory.inventory_booking_date) }}
                <span v-if="inventory.pick_up_location" class="pickup-info">
                  📍 {{ inventory.pick_up_location }}
                </span>
              </div>
              <div class="summary-item-total">
                Total: {{ inventory.inventory_booking_currency }} {{ $roundOff(inventory.inventory_booking_total_cost) }}
              </div>
              <div class="summary-item-nights-guests">
                <span>Guests: {{ inventory.inventory_booking_guests }}</span>
              </div>
              <div class="summary-item-icon">
                <v-icon small color="#74a533">mdi-hiking</v-icon>
              </div>
            </v-container>
          </v-card>
        </v-container>
      </v-flex>
    </v-layout>

    <v-dialog v-model="edit_accommodation_dates_dialog"
              persistent
              max-width="600px"
              class="edit-dialog">
      <v-card flat class="summary-card white-card">
        <v-card-title class="knockout-header black--text">
          EDIT ACCOMMODATION DATES
        </v-card-title>
        <hr />
        <v-card-text>
          <v-container>
            <v-flex xs12>
              <p class="subheading"><b>{{ edit_accommodation_dates_data.accommodation_name }}</b></p>
            </v-flex>
            <v-flex xs12>
              <v-menu v-model="edit_accommodation_dates_data.check_in_date_dialog" :close-on-content-click="false" :nudge-right="40" lazy transition="scale-transition" offset-y full-width max-width="290px" min-width="290px">
                <template slot="activator">
                  <v-text-field v-model="edit_accommodation_dates_data.check_in_date" label="Check-In Date" hint="YYYY/MM/DD" persistent-hint></v-text-field>
                </template>
                <v-date-picker color="primary" v-model="edit_accommodation_dates_data.check_in_date" no-title @input="edit_accommodation_dates_data.check_in_date_dialog = false"></v-date-picker>
              </v-menu>
            </v-flex>
            <v-flex xs12>
              <v-menu v-model="edit_accommodation_dates_data.check_out_date_dialog" :close-on-content-click="false" :nudge-right="40" lazy transition="scale-transition" offset-y full-width max-width="290px" min-width="290px">
                <template slot="activator">
                  <v-text-field v-model="edit_accommodation_dates_data.check_out_date" label="Check-Out Date" hint="YYYY/MM/DD" persistent-hint></v-text-field>
                </template>
                <v-date-picker color="primary" v-model="edit_accommodation_dates_data.check_out_date" no-title @input="edit_accommodation_dates_data.check_out_date_dialog = false"></v-date-picker>
              </v-menu>
            </v-flex>
          </v-container>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn color="red darken-1" flat class="arial-text black--text action-btn" @click="edit_accommodation_dates_dialog = false" style="letter-spacing: 1px;">
            CLOSE
          </v-btn>
          <v-btn color="green darken-1" flat class="arial-text black--text action-btn" @click="updateAccommodationDates" style="letter-spacing: 1px;">
            SAVE
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="edit_activity_date_dialog"
              persistent
              max-width="600px"
              class="edit-dialog">
      <v-card flat class="summary-card white-card">
        <v-card-title class="knockout-header black--text">
          EDIT ACTIVITY DATE
        </v-card-title>
        <hr />
        <v-card-text>
          <v-container>
            <v-flex xs12>
              <p class="subheading"><b>{{ edit_activity_date_data.activity_name }}</b></p>
            </v-flex>
            <v-flex xs12>
              <v-menu v-model="edit_activity_date_data.date_dialog" :close-on-content-click="false" :nudge-right="40" lazy transition="scale-transition" offset-y full-width max-width="290px" min-width="290px">
                <template slot="activator">
                  <v-text-field v-model="edit_activity_date_data.activity_date" label="Activity Date" hint="YYYY/MM/DD" persistent-hint></v-text-field>
                </template>
                <v-date-picker color="primary" v-model="edit_activity_date_data.activity_date" no-title @input="edit_activity_date_data.date_dialog = false"></v-date-picker>
              </v-menu>
            </v-flex>
          </v-container>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn color="red darken-1" flat class="arial-text black--text action-btn" @click="edit_activity_date_dialog = false" style="letter-spacing: 1px;">
            CLOSE
          </v-btn>
          <v-btn color="green darken-1" flat class="arial-text black--text action-btn" @click="updateActivityDate" style="letter-spacing: 1px;">
            SAVE
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <template>
      <v-layout row wrap>
        <v-flex lg4 md4 sm12 xs12>
          <v-container>
            <v-card flat class="summary-card" style="background: linear-gradient(135deg, #F8B229 0%, #E89F1A 100%)">
              <div class="card-header">
                <p class="arial-text white--text">CURRENCY BOOKED IN</p>
              </div>
              <div class="card-body">
                <p class="arial-text white--text text-big font-light">
                  {{ singleBooking.currency }}
                  {{ $roundOff(singleBooking.total_cost.toFixed(2)).replace(/(\d)(?=(\d{3})+\.)/g, "$1,") }}
                </p>
              </div>
            </v-card>

            <v-card flat
                    class="summary-card"
                    style="background: linear-gradient(135deg, #FF9800 0%, #F57C00 100%)"
                    v-if="singleBooking.booking_payment_status === 'Not Paid'">
              <div class="card-header">
                <p class="arial-text white--text">{{ singleBooking.booking_payment_status.toUpperCase() }}</p>
              </div>
              <div class="card-body">
                <p class="arial-text white--text text-big font-light">
                  Balance: {{ $localCurrency }}
                  {{ $roundOff(convertBasedBookingCreationDate(singleBooking.currency, singleBooking.booking_payment_balance, singleBooking.currency_buying_rate_at_time, singleBooking.currency_selling_rate_at_time).amount).toLocaleString("en") }}
                </p>
                <p class="arial-text white--text">
                  Total: {{ $localCurrency }}
                  {{ $roundOff(convertBasedBookingCreationDate(singleBooking.currency, singleBooking.total_cost, singleBooking.currency_buying_rate_at_time, singleBooking.currency_selling_rate_at_time).amount).toLocaleString("en") }}
                </p>
              </div>
            </v-card>

            <v-card flat
                    class="summary-card"
                    style="background: linear-gradient(135deg, #1976D2 0%, #1565C0 100%)"
                    v-if="singleBooking.booking_payment_status === 'Paid'">
              <div class="card-header">
                <p class="arial-text white--text">{{ singleBooking.booking_payment_status.toUpperCase() }}</p>
              </div>
              <div class="card-body">
                <p class="arial-text white--text text-big font-light">
                  Total: {{ $localCurrency }}
                  {{ $roundOff(convertBasedBookingCreationDate(singleBooking.currency, singleBooking.total_cost, singleBooking.currency_buying_rate_at_time, singleBooking.currency_selling_rate_at_time).amount) }}
                </p>
              </div>
            </v-card>

            <v-card flat
                    class="summary-card"
                    style="background: linear-gradient(135deg, #8c7ae6 0%, #7b68ee 100%)"
                    v-if="singleBooking.booking_payment_status === 'Complimentary'">
              <div class="card-header">
                <p class="arial-text white--text">{{ singleBooking.booking_payment_status.toUpperCase() }}</p>
              </div>
              <div class="card-body">
                <p class="arial-text white--text text-big font-light">
                  Total: {{ $localCurrency }}
                  {{ $roundOff(convertBasedBookingCreationDate(singleBooking.currency, singleBooking.total_cost, singleBooking.currency_buying_rate_at_time, singleBooking.currency_selling_rate_at_time).amount) }}
                </p>
              </div>
            </v-card>

            <v-card flat
                    class="summary-card"
                    style="background: linear-gradient(135deg, #9b59b6 0%, #8e44ad 100%)"
                    v-if="singleBooking.booking_payment_status === 'To Invoice'">
              <div class="card-header">
                <p class="arial-text white--text">{{ singleBooking.booking_payment_status.toUpperCase() }}</p>
              </div>
              <div class="card-body">
                <p class="arial-text white--text text-big font-light">
                  Total: {{ $localCurrency }}
                  {{ $roundOff(convertBasedBookingCreationDate(singleBooking.currency, singleBooking.total_cost, singleBooking.currency_buying_rate_at_time, singleBooking.currency_selling_rate_at_time).amount) }}
                </p>
              </div>
            </v-card>

            <v-card flat
                    class="summary-card"
                    style="background: linear-gradient(135deg, #487eb0 0%, #3f6e9a 100%)"
                    v-if="singleBooking.booking_payment_status === 'Incomplete Payment'">
              <div class="card-header">
                <p class="arial-text white--text">{{ singleBooking.booking_payment_status.toUpperCase() }}</p>
              </div>
              <div class="card-body">
                <p class="arial-text white--text text-big font-light">
                  Balance: {{ $localCurrency }}
                  {{ $roundOff(convertBasedBookingCreationDate(singleBooking.currency, singleBooking.booking_payment_balance, singleBooking.currency_buying_rate_at_time, singleBooking.currency_selling_rate_at_time).amount) }}
                </p>
                <p class="arial-text white--text">
                  Total: {{ $localCurrency }}
                  {{ $roundOff(convertBasedBookingCreationDate(singleBooking.currency, singleBooking.total_cost, singleBooking.currency_buying_rate_at_time, singleBooking.currency_selling_rate_at_time).amount) }}
                </p>
              </div>
            </v-card>

            <br v-if="singleBooking.credit_note_issued" />
            <v-card flat
                    class="summary-card"
                    style="background: linear-gradient(135deg, #107c22 0%, #0d6b1c 100%)"
                    v-if="singleBooking.credit_note_issued">
              <div class="card-header">
                <p class="arial-text white--text">CREDIT NOTE ISSUED</p>
              </div>
              <div class="card-body">
                <p class="arial-text white--text text-big font-light">
                  <span v-if="singleBooking.credit_notes">
                    {{ formated_date(singleBooking.credit_notes[0].created_at) }}
                  </span>
                </p>
                <p class="arial-text white--text" v-if="singleBooking.credit_notes">
                  {{ singleBooking.credit_notes[0].credit_note_reason }}
                </p>
              </div>
            </v-card>

            <v-card flat
                    class="summary-card"
                    style="background: linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)"
                    v-if="singleBooking.actual_booking_check_in_date !== null">
              <div class="card-header">
                <p class="arial-text white--text">GATE ENTRY DETAILS</p>
              </div>
              <div class="card-body">
                <p class="arial-text white--text text-big font-light">
                  Checked In: {{ formated_date_time(singleBooking.actual_booking_check_in_date) }}
                </p>
                <p class="arial-text white--text" v-if="singleBooking.gate_entry_point">
                  Gate: {{ singleBooking.gate_entry_point }}
                </p>
              </div>
            </v-card>

            <v-card flat
                    class="summary-card"
                    style="background: linear-gradient(135deg, #1976D2 0%, #1565C0 100%)"
                    v-if="singleBooking.actual_booking_check_in_date !== null">
              <div class="card-header">
                <p class="arial-text white--text">DATE CHECKED IN</p>
              </div>
              <div class="card-body">
                <p class="arial-text white--text text-big font-light">
                  {{ formated_date(singleBooking.actual_booking_check_in_date) }}
                </p>
              </div>
            </v-card>

            <v-card flat
                    class="summary-card"
                    style="background: linear-gradient(135deg, #1976D2 0%, #1565C0 100%)"
                    v-if="singleBooking.actual_booking_check_in_date === null">
              <div class="card-header">
                <p class="arial-text white--text">CHECK IN DATE</p>
              </div>
              <div class="card-body">
                <p class="arial-text white--text text-big font-light">
                  {{ formated_date(singleBooking.booking_check_in_date) }}
                </p>
              </div>
            </v-card>

            <v-card flat
                    class="summary-card"
                    style="background: linear-gradient(135deg, #FF9800 0%, #F57C00 100%)"
                    v-if="singleBooking.actual_booking_check_out_date === null">
              <div class="card-header">
                <p class="arial-text white--text">CHECK OUT DATE</p>
              </div>
              <div class="card-body">
                <p class="arial-text white--text text-big font-light">
                  {{ formated_date(singleBooking.booking_check_out_date) }}
                </p>
              </div>
            </v-card>

            <v-card flat
                    class="summary-card"
                    style="background: linear-gradient(135deg, #FF9800 0%, #F57C00 100%)"
                    v-if="singleBooking.actual_booking_check_out_date !== null">
              <div class="card-header">
                <p class="arial-text white--text">DATE CHECKED OUT</p>
              </div>
              <div class="card-body">
                <p class="arial-text white--text text-big font-light">
                  {{ formated_date(singleBooking.actual_booking_check_out_date) }}
                </p>
              </div>
            </v-card>
          </v-container>
        </v-flex>

        <v-flex lg8 md8 sm12 xs12 flat>
          <template>
            <v-container class="booking-container">
              <v-tabs v-model="active" color="primary" dark slider-color="green">
                <v-tab key="details" ripple class="knockout-text tab-header">
                  BOOKING DETAILS
                </v-tab>
                <v-tab key="notes" ripple class="knockout-text tab-header">
                  BOOKING NOTES
                </v-tab>
                <v-tab key="logs" ripple class="knockout-text tab-header">
                  LOG
                </v-tab>
                <v-tab key="transactions" ripple class="knockout-text tab-header">
                  TRANSACTIONS
                </v-tab>
                <v-tab key="credit_notes" ripple class="knockout-text tab-header">
                  CREDIT NOTES
                </v-tab>
                <v-tab key="inspection_notes" ripple class="knockout-text tab-header">
                  INSPECTION NOTES
                </v-tab>
                <v-tab key="residency_proof" ripple class="knockout-text tab-header">
                  PROOF OF RESIDENCY
                </v-tab>

                <v-tab-item key="details">
                  <v-card flat class="summary-card">
                    <v-container>
                      <v-layout wrap class="status-chips">
                        <v-chip v-if="singleBooking.status === 'Cancelled'" color="#e74c3c" text-color="white" class="mr-2 mb-2">
                          <v-avatar left><v-icon>cancel</v-icon></v-avatar>
                          {{singleBooking.status}}
                        </v-chip>
                        <v-chip v-if="singleBooking.status === 'To Invoice'" color="#9b59b6" text-color="white" class="mr-2 mb-2">
                          <v-avatar left><v-icon>timelapse</v-icon></v-avatar>
                          {{singleBooking.status}}
                        </v-chip>
                        <v-chip v-if="singleBooking.status === 'Unconfirmed'" color="#f0932b" text-color="white" class="mr-2 mb-2">
                          <v-avatar left><v-icon>loop</v-icon></v-avatar>
                          {{singleBooking.status}}
                        </v-chip>
                        <v-chip v-if="singleBooking.status === 'Checked In'" color="#3498db" text-color="white" class="mr-2 mb-2">
                          <v-avatar left><v-icon>explore</v-icon></v-avatar>
                          {{singleBooking.status}}
                        </v-chip>
                        <v-chip v-if="singleBooking.status === 'Checked Out'" color="#2ecc71" text-color="white" class="mr-2 mb-2">
                          <v-avatar left><v-icon>explore_off</v-icon></v-avatar>
                          {{singleBooking.status}}
                        </v-chip>
                        <v-chip v-if="singleBooking.status === 'Confirmed'" color="#0084ff" text-color="white" class="mr-2 mb-2">
                          <v-avatar left><v-icon>check_circle</v-icon></v-avatar>
                          {{singleBooking.status}}
                        </v-chip>
                        <v-chip v-if="singleBooking.status === 'Deposit'" color="#487eb0" text-color="white" class="mr-2 mb-2">
                          <v-avatar left><v-icon>payment</v-icon></v-avatar>
                          {{singleBooking.status}}
                        </v-chip>
                        <v-chip v-if="singleBooking.status === 'Updated'" color="#00cec9" text-color="white" class="mr-2 mb-2">
                          <v-avatar left><v-icon>edit</v-icon></v-avatar>
                          {{singleBooking.status}}
                        </v-chip>
                        <v-chip v-if="singleBooking.status === 'Complimentary'" color="#8c7ae6" text-color="white" class="mr-2 mb-2">
                          <v-avatar left><v-icon>remove_circle</v-icon></v-avatar>
                          {{singleBooking.status}}
                        </v-chip>
                        <v-chip v-if="singleBooking.status === 'Abandoned'" color="#5f27cd" text-color="white" class="mr-2 mb-2">
                          <v-avatar left><v-icon>remove_circle</v-icon></v-avatar>
                          {{singleBooking.status}}
                        </v-chip>
                        <v-chip v-if="singleBooking.status === 'Postponed'" color="#800080" text-color="white" class="mr-2 mb-2">
                          <v-avatar left><v-icon>remove_circle</v-icon></v-avatar>
                          {{singleBooking.status}}
                        </v-chip>
                        <v-chip v-if="singleBooking.status === 'Updated' && singleBooking.booking_payment_status === 'Incomplete Payment'" color="#487eb0" text-color="white" class="mr-2 mb-2">
                          <v-avatar left><v-icon>payment</v-icon></v-avatar>
                          Deposit
                        </v-chip>
                        <v-chip v-if="singleBooking.status === 'Postponed' && singleBooking.booking_payment_status === 'Incomplete Payment'" color="#487eb0" text-color="white" class="mr-2 mb-2">
                          <v-avatar left><v-icon>payment</v-icon></v-avatar>
                          Deposit
                        </v-chip>
                        <v-chip v-if="singleBooking.booking_payment_status === 'Not Paid'" color="#cd201f" text-color="white" class="mr-2 mb-2">
                          <v-avatar left><v-icon>cancel</v-icon></v-avatar>
                          {{singleBooking.booking_payment_status}}
                        </v-chip>
                        <v-chip v-if="singleBooking.booking_payment_status === 'Paid'" color="#00c300" text-color="white" class="mr-2 mb-2">
                          <v-avatar left><v-icon>check_circle</v-icon></v-avatar>
                          {{singleBooking.booking_payment_status}}
                        </v-chip>
                        <v-chip v-if="singleBooking.booking_payment_status === 'To Invoice'" color="#9b59b6" text-color="white" class="mr-2 mb-2">
                          <v-avatar left><v-icon>timelapse</v-icon></v-avatar>
                          {{singleBooking.booking_payment_status}}
                        </v-chip>
                        <v-chip v-if="singleBooking.booking_payment_status === 'Complimentary'" color="#8c7ae6" text-color="white" class="mr-2 mb-2">
                          <v-avatar left><v-icon>remove_circle</v-icon></v-avatar>
                          {{singleBooking.booking_payment_status}}
                        </v-chip>
                        <v-chip v-if="singleBooking.credit_note_issued" color="#107c22" text-color="white" class="mr-2 mb-2">
                          <v-avatar left><v-icon>credit_card</v-icon></v-avatar>
                          Credit Note Issued
                        </v-chip>
                      </v-layout>

                      <v-layout v-if="(singleBooking.booking_payments).length == 0 &&
                        singleBooking.status !== 'Cancelled' &&
                        is_past_booking == 'False' &&
                        singleBooking.status !== 'No-Show' &&
                        localData.user_type_id !== '35fcfdaf' && localData.user_type_id !== 'acc1886a' &&
                        $foc === true" wrap>
                        <v-flex lg4 md4 xs12>
                          <v-select :items="statuses"
                                    label="CHANGE STATUS"
                                    class="arial-text status-select"
                                    @input="changeStatus"></v-select>
                        </v-flex>
                      </v-layout>

                      <template>
                        <div>
                          <v-layout row wrap>
                            <v-flex lg6 md6 xs12>
                              <v-card flat class="beautiful-info-card mb-4">
                                <div class="info-card-header">
                                  <v-icon left dark small>mdi-ticket</v-icon>
                                  BOOKING REFERENCE
                                </div>
                                <div class="info-card-body">
                                  <div class="ref-code">{{ singleBooking.booking_ref_code }}</div>
                                  <div class="info-row"><span class="info-label">Booking by:</span> {{ singleBooking.session_user }}</div>
                                  <div class="info-row"><span class="info-label">Created at:</span> {{ formated_date_time(singleBooking.created_at) }}</div>
                                  <div v-if="singleBooking.partner_booking_ref !== null" class="info-row"><span class="info-label">Partner Booking Ref:</span> {{ singleBooking.partner_booking_ref }}</div>
                                  <div v-if="singleBooking.paid_by != null" class="info-row"><span class="info-label">Payment Detail:</span> {{ singleBooking.paid_by }}</div>
                                </div>
                              </v-card>
                            </v-flex>
                            <v-flex lg6 md6 xs12>
                              <v-card flat class="beautiful-info-card mb-4">
                                <div class="info-card-header">
                                  <v-icon left dark small>mdi-account-box</v-icon>
                                  CLIENT DETAILS
                                </div>
                                <div class="info-card-body">
                                  <div class="info-row"><span class="info-label">First name:</span> {{ singleBooking.booking_details.first_name }}</div>
                                  <div class="info-row"><span class="info-label">Last name:</span> {{ singleBooking.booking_details.last_name }}</div>
                                  <div class="info-row"><span class="info-label">Phone Number:</span> {{ singleBooking.booking_details.phone_number }}</div>
                                  <div class="info-row"><span class="info-label">Email:</span> {{ singleBooking.booking_details.email_address }}</div>
                                  <div class="info-row"><span class="info-label">Destination:</span> {{ singleBooking.destination }}</div>
                                  <div class="info-row"><span class="info-label">Address:</span> {{ singleBooking.booking_details.address }}</div>
                                  <div class="info-row" v-if="singleBooking.school_booking === false">
                                    <span class="info-label">KRA PIN:</span>
                                    {{ singleBooking.booking_details.kra_pin_number }}
                                  </div>
                                  <div class="info-row"><span class="info-label">Client Note:</span> {{ singleBooking.booking_details.additional_note }}</div>

                                  <div v-if="singleBooking.school_booking === true" class="sub-details">
                                    <div class="sub-details-title">SCHOOL DETAILS</div>
                                    <div class="info-row"><span class="info-label">School Name:</span> {{ singleBooking.school_booking_details[0].school_name }}</div>
                                    <div class="info-row"><span class="info-label">School Level:</span> {{ singleBooking.school_booking_details[0].school_level }}</div>
                                    <div class="info-row"><span class="info-label">School Code:</span> {{ singleBooking.school_booking_details[0].school_code }}</div>
                                    <div class="info-row"><span class="info-label">School Type:</span> {{ singleBooking.school_booking_details[0].school_type }}</div>
                                    <div class="info-row"><span class="info-label">Teacher Name:</span> {{ singleBooking.school_booking_details[0].primary_contact }}</div>
                                    <div class="info-row"><span class="info-label">Teacher Email:</span> {{ singleBooking.school_booking_details[0].primary_contact_email }}</div>
                                    <div class="info-row"><span class="info-label">KRA PIN:</span> {{ singleBooking.school_booking_details[0].kra_pin }}</div>
                                    <div class="info-row"><span class="info-label">Teacher Phone Number:</span> {{ singleBooking.school_booking_details[0].primary_contact_phone }}</div>
                                  </div>

                                  <div v-if="singleBooking.corporate_booking === true" class="sub-details">
                                    <div class="sub-details-title">CORPORATE DETAILS</div>
                                    <div class="info-row"><span class="info-label">Company Name:</span> {{ singleBooking.corporate_booking_details[0].organisation_name }}</div>
                                  </div>

                                  <div v-if="singleBooking.partner_name !== null" class="sub-details">
                                    <div class="sub-details-title">PARTNER DETAILS</div>
                                    <div class="info-row"><span class="info-label">Partner Name:</span> {{ singleBooking.partner_name }}</div>
                                    <div class="info-row"><span class="info-label">Agent Email Address:</span> {{ singleBooking.agent_email }}</div>
                                  </div>
                                </div>
                              </v-card>
                            </v-flex>
                          </v-layout>
                        </div>
                      </template>

                      <v-card flat class="beautiful-info-card mb-4" v-if="singleBooking.booking_details.entry_mode || singleBooking.vehicle_details.length > 0 || singleBooking.actual_booking_check_in_date !== null">
                        <div class="info-card-header">
                          <v-icon left dark small>mdi-door-open</v-icon>
                          ENTRY POINT
                        </div>
                        <div class="info-card-body">
                          <v-container v-if="singleBooking.booking_details.entry_mode">
                            <div class="info-row"><span class="info-label">Entry Mode:</span> {{ singleBooking.booking_details.entry_mode_name }}</div>
                            <div class="info-row"><span class="info-label">Entry Point:</span> {{ singleBooking.booking_details.gate_name }}</div>
                            <div class="info-row"><span class="info-label">Mode Registration Details:</span> {{ singleBooking.booking_details.mode_registration }}</div>
                          </v-container>
                          <v-container v-if="singleBooking.vehicle_details && singleBooking.vehicle_details.length > 0">
                            <div v-for="(details, idx) in singleBooking.vehicle_details" :key="idx" class="mb-2">
                              <div class="info-row"><span class="info-label">Driver's Name:</span> {{ details.check_vehicle_driver }}</div>
                              <div class="info-row"><span class="info-label">Driver's Phone:</span> {{ details.check_vehicle_phone_number }}</div>
                              <div class="info-row"><span class="info-label">Vehicle Reg:</span> {{ details.check_vehicle_reg }}</div>
                              <div class="info-row"><span class="info-label">Vehicle Sensor Id:</span> {{ details.check_vehicle_disc }}</div>
                              <div class="info-row"><span class="info-label">Mode Of Entry:</span> {{ details.entry_mode }}</div>
                              <div class="info-row"><span class="info-label">Source:</span> {{ details.gatepass_source }}</div>
                              <hr v-if="idx < singleBooking.vehicle_details.length - 1" class="divider" />
                            </div>
                          </v-container>
                          <v-container v-if="singleBooking.actual_booking_check_in_date !== null">
                            <div class="info-row"><span class="info-label">Checked In:</span> {{ formated_date_time(singleBooking.actual_booking_check_in_date) }}</div>
                            <span v-if="singleBooking.gate_entry_point" class="info-row"><span class="info-label">Gate:</span> {{ singleBooking.gate_entry_point }}</span>
                          </v-container>
                        </div>
                      </v-card>

                      <v-card flat class="beautiful-info-card donation-large mb-4" v-if="singleBooking.donations && singleBooking.donations.length">
                        <div class="info-card-header">
                          <v-icon left dark small>mdi-hand-heart</v-icon>
                          DONATION ({{ singleBooking.donations.length }})
                        </div>
                        <div class="info-card-body">
                          <ul class="details-list">
                            <li v-for="(donation, key) in singleBooking.donations"
                                :key="key"
                                class="detail-item">
                              <router-link :to="{ name: 'Donation', params: { donation_id: donation.donation_public_id } }">View Donation</router-link>
                              | Cause: {{ donation.donation_cause_name }} –
                              {{ donation.donation_currency_name }} {{ donation.donation_amount }}
                            </li>
                          </ul>
                        </div>
                      </v-card>

                      <h4 class="earth-color arial-text section-title">OFFICE NOTES</h4>
                      <v-card v-for="(note, key) in singleBooking.booking_notes" :key="key" class="mb-4 note-card">
                        <v-container fluid class="booking-summary-container">
                          <v-chip small color="primary" text-color="white" v-if="note.show_on_invoice === true" class="mb-2 note-tag">
                            public
                          </v-chip>
                          <v-chip small v-if="note.show_on_invoice === false" class="mb-2 note-tag internal">
                            internal
                          </v-chip>
                          <p class="subheading arial-text note-content">
                            <span class="black--text">Note:</span> {{ note.note }}
                          </p>
                          <p class="arial-text note-meta">
                            <span class="grey--text">Note by:</span> {{ note.user }}
                            <span class="grey--text">at</span> {{ formated_date_time(note.created_at) }}
                          </p>
                        </v-container>
                      </v-card>

                    </v-container>
                  </v-card>
                </v-tab-item>

                <v-tab-item key="notes">
                  <v-card flat class="summary-card">
                    <v-container>
                      <v-form @submit.prevent="postNote()" class="note-form">
                        <v-textarea label="NOTE" v-model="note.note" class="arial-text note-input"></v-textarea>
                        <v-switch color="primary" label="SHOW ON INVOICE" v-model="note.show_on_invoice" class="arial-text note-switch"></v-switch>
                        <v-btn depressed color="primary" block type="submit" class="arial-text submit-note-btn">
                          POST NOTE
                        </v-btn>
                      </v-form>
                    </v-container>
                  </v-card>

                  <h4 class="earth-color arial-text section-title" v-if="singleBooking.booking_notes.length > 0">
                    NOTES
                  </h4>
                  <v-card flat v-for="(note, key) in singleBooking.booking_notes" :key="key" class="mb-4 note-card">
                    <v-container fluid class="booking-summary-container">
                      <v-chip small color="primary" text-color="white" v-if="note.show_on_invoice === true" class="mb-2 note-tag">
                        public
                      </v-chip>
                      <v-chip small v-if="note.show_on_invoice === false" class="mb-2 note-tag internal">
                        internal
                      </v-chip>
                      <p class="subheading arial-text note-content">
                        <span class="grey--text">Note:</span> {{ note.note }}
                      </p>
                      <p class="arial-text note-meta">
                        <span class="grey--text">Note by:</span> {{ note.user }}
                        <span class="grey--text">at</span> {{ formated_date_time(note.created_at) }}
                      </p>
                    </v-container>
                  </v-card>
                </v-tab-item>

                <v-tab-item key="logs">
                  <v-card flat class="summary-card">
                    <v-data-table :headers="columns"
                                  :items="singleBooking.booking_activity"
                                  :search="search"
                                  :rows-per-page-items="[10, 25, { text: 'All', value: -1 }]"
                                  :disable-initial-sort="true"
                                  :loading="$loadingTrue"
                                  class="arial-text log-table">
                      <template slot="items" slot-scope="props">
                        <td>{{ formated_date_time(props.item.created_at) }}</td>
                        <td>{{ props.item.booking_activity_description }}</td>
                        <td>{{ props.item.session_user }}</td>
                      </template>
                    </v-data-table>
                  </v-card>
                </v-tab-item>

                <v-tab-item key="transactions">
                  <v-card flat class="summary-card">
                    <v-data-table :headers="transactionColumns"
                                  :items="singleBooking.booking_payments"
                                  :search="search"
                                  :rows-per-page-items="[10, 25, { text: 'All', value: -1 }]"
                                  :disable-initial-sort="true"
                                  :loading="$loadingTrue"
                                  class="arial-text transaction-table">
                      <template slot="items" slot-scope="props">
                        <td>{{ formated_date_time(props.item.transaction_date) }}</td>
                        <td>{{ props.item.payment_method }}</td>
                        <td>{{ props.item.transaction_code }}</td>
                        <td>
                          {{ props.item.transaction_total_currency }}
                          {{ props.item.transaction_original_cost.toFixed(2).replace(/(\d)(?=(\d{3})+\.)/g, "$1,") }}
                        </td>
                        <td>
                          {{ props.item.transaction_payment_currency }}
                          {{ props.item.transaction_total.toFixed(2).replace(/(\d)(?=(\d{3})+\.)/g, "$1,") }}
                        </td>
                        <td>{{ $roundOff(props.item.transaction_balance) }}</td>
                        <td>{{ props.item.session_user }}</td>
                      </template>
                    </v-data-table>
                  </v-card>
                </v-tab-item>

                <v-tab-item key="credit_notes">
                  <v-card flat class="summary-card">
                    <v-data-table :headers="creditNoteColumns"
                                  :items="singleBooking.credit_notes"
                                  :search="search"
                                  :loading="$loadingTrue"
                                  :disable-initial-sort="true"
                                  :rows-per-page-items="[10, 25, { text: 'All', value: -1 }]"
                                  class="arial-text credit-note-table">
                      <template slot="items" slot-scope="props">
                        <td>{{ props.item.credit_note_ref }}</td>
                        <td>
                          {{ props.item.credit_note_currency_name }}
                          {{ props.item.credit_note_amount.toFixed(2).replace(/(\d)(?=(\d{3})+\.)/g, "$1,") }}
                        </td>
                        <td>{{ props.item.credit_note_reason }}</td>
                        <td>{{ props.item.user }}</td>
                        <td>{{ formated_date_time(props.item.created_at) }}</td>
                      </template>
                    </v-data-table>
                  </v-card>
                </v-tab-item>

                <v-tab-item key="inspection_notes">
                  <v-card flat class="summary-card">
                    <v-data-table :headers="inspectionNoteColumns"
                                  :items="singleBooking.booking_ranger_notes"
                                  :search="search"
                                  :loading="$loadingTrue"
                                  :disable-initial-sort="true"
                                  :rows-per-page-items="[10, 25, { text: 'All', value: -1 }]"
                                  class="arial-text inspection-note-table">
                      <template slot="items" slot-scope="props">
                        <td>{{ props.item.note }}</td>
                        <td>
                          <v-chip color="#cd201f" text-color="white" v-if="props.item.alert == 'true'">
                            Alert
                          </v-chip>
                          <v-chip color="primary" text-color="white" v-else>
                            Okay
                          </v-chip>
                        </td>
                        <td>{{ props.item.user }}</td>
                        <td>{{ formated_date_time(props.item.created_at) }}</td>
                      </template>
                    </v-data-table>
                  </v-card>
                </v-tab-item>

                <v-tab-item key="residency_proof">
                  <v-card flat class="summary-card">
                    <v-data-table :headers="residencyProofColumns"
                                  :items="singleBooking.residency_proof"
                                  :search="search"
                                  :loading="$loadingTrue"
                                  :disable-initial-sort="true"
                                  :rows-per-page-items="[10, 25, { text: 'All', value: -1 }]"
                                  class="arial-text residency-proof-table">
                      <template slot="items" slot-scope="props">
                        <td>{{ props.item.first_name }} {{ props.item.last_name }}</td>
                        <td>{{ props.item.payment_person }}</td>
                        <td>{{ props.item.proof_point }}</td>
                        <td>{{ props.item.document_id }}</td>
                        <td>
                          <v-chip :color="props.item.verified ? 'success' : 'error'" small text-color="white">
                            {{ props.item.verified ? 'Yes' : 'No' }}
                          </v-chip>
                        </td>
                        <td>{{ props.item.inspected_by || 'System' }}</td>
                        <td>{{ formated_date_time(props.item.created_at) }}</td>
                        <td>
                          <v-btn v-if="props.item.has_image"
                                 small
                                 icon
                                 color="primary"
                                 @click="viewVerificationImage(props.item)">
                            <v-icon small>mdi-eye</v-icon>
                          </v-btn>
                          <v-btn v-else
                                 small
                                 icon
                                 disabled
                                 color="grey">
                            <v-icon small>mdi-eye-off</v-icon>
                          </v-btn>
                        </td>
                      </template>
                    </v-data-table>
                  </v-card>
                </v-tab-item>
              </v-tabs>
              <div class="text-xs-center mt-3"></div>
            </v-container>
          </template>
        </v-flex>
      </v-layout>
    </template>

    {{ $getBuyingRateBasedCreationDate }}

    <v-dialog v-model="verificationDialog" persistent max-width="500px">
      <v-card style="border-radius: 16px;">
        <v-card-title class="headline" style="background: #74a533; color: white; border-radius: 16px 16px 0 0;">
          Verify Citizen Adult
          <v-spacer></v-spacer>
          <v-btn icon dark @click="verificationDialog = false"><v-icon>close</v-icon></v-btn>
        </v-card-title>
        <v-card-text class="mt-4">
          <v-container>
            <v-flex xs12><v-text-field label="National ID Number" v-model="verificationData.id_number" required outline dense type="number" :loading="verifying" :disabled="verifying"></v-text-field></v-flex>
            <v-flex xs12><v-text-field label="National ID Serial Number" v-model="verificationData.serial_number" required outline dense :loading="verifying" :disabled="verifying"></v-text-field></v-flex>
            <v-alert v-if="verificationResult" :type="verificationResult.type" class="mt-3" dense>{{ verificationResult.message }}</v-alert>
          </v-container>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn flat @click="verificationDialog = false" :disabled="verifying">Cancel</v-btn>
          <v-btn style="background: #74a533; color: white;" @click="verifyCitizen" :loading="verifying" :disabled="verifying">Verify</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="print_credit_note_dialog" persistent max-width="600px">
      <v-card flat class="summary-card white-card">
        <v-card-title class="knockout-header black--text">
          SELECT CREDIT NOTE TO PRINT
        </v-card-title>
        <hr />
        <v-card-text>
          <v-flex xs12>
            <v-select label="Credit Note Ref"
                      :items="singleBooking.credit_notes"
                      item-text="credit_note_ref"
                      item-value="credit_note_public_id"
                      v-model="print_credit_note.credit_note_id"
                      outline
                      class="arial-text"></v-select>
          </v-flex>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn color="red darken-1"
                 flat
                 class="arial-text black--text action-btn"
                 @click="print_credit_note_dialog = false"
                 style="letter-spacing: 1px;">
            CLOSE
          </v-btn>
          <v-btn color="green darken-1"
                 flat
                 class="arial-text black--text action-btn"
                 @click="printCreditNote"
                 style="letter-spacing: 1px;">
            PRINT
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="credit_dialog"
              fullscreen
              hide-overlay
              transition="dialog-bottom-transition"
              scrollable>
      <v-card tile>
        <v-toolbar card dark color="primary">
          <v-btn icon dark @click="closeCreditDialog()">
            <v-icon>close</v-icon>
          </v-btn>
          <v-toolbar-title class="earth">Issue A Credit Note</v-toolbar-title>
          <v-spacer></v-spacer>
        </v-toolbar>
        <v-card-text>
          <v-list three-line subheader>
            <v-layout row wrap>
              <v-container>
                <v-tabs color="default" slider-color="green">
                  <v-tab key="overview" ripple class="earth">BOOKING OVERVIEW</v-tab>
                  <v-tab key="guests" ripple class="earth">CONSERVANCY FEES</v-tab>
                  <v-tab key="vehicles" ripple class="earth">VEHICLE FEES</v-tab>
                  <v-tab key="accommodations" ripple class="earth">ACCOMMODATION FEES</v-tab>
                  <v-tab key="external_accommodations" ripple class="earth">OL PEJETA ESCAPES FEES</v-tab>
                  <v-tab key="activities" ripple class="earth">ACTIVITY FEES</v-tab>
                  <v-tab key="summary" ripple class="earth">SUMMARY</v-tab>
                  <v-tab-item key="overview">
                    <div class="table table-responsive m-t">
                      <h4 class="earth-color">CONSERVANCY FEES</h4>
                      <table class="table table-striped">
                        <thead>
                          <tr>
                            <td><b>Residency</b></td>
                            <td><b>Guests</b></td>
                            <td><b>Amount</b></td>
                            <td><b>Discount%</b></td>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="(guest, key) in singleBooking.guests" v-if="guest.payment_guests > 0" :key="key">
                            <td>{{ guest.payment_person }}</td>
                            <td>{{ guest.payment_guests }}</td>
                            <td>{{ guest.payment_person_currency }} {{ $roundOff(guest.payment_guests * guest.payment_person_amount * guest.no_of_nights * ((100 - guest.payment_person_discount) / 100)) }}</td>
                            <td>{{ guest.payment_person_discount }}%</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <div class="table table-responsive m-t">
                      <h4 class="earth-color">CONSERVANCY VEHICLE FEES</h4>
                      <table class="table table-striped">
                        <thead>
                          <tr>
                            <td><b>Vehicle Type</b></td>
                            <td><b>No. of Vehicles</b></td>
                            <td><b>Amount</b></td>
                            <td><b>Discount %</b></td>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="(vehicle, key) in singleBooking.vehicles" v-if="vehicle.vehicles > 0" :key="key">
                            <td>{{ vehicle.vehicle_charge_category }}</td>
                            <td>{{ vehicle.vehicles }}</td>
                            <td>{{ singleBooking.currency }} {{ $roundOff(convert1(vehicle.vehicle_charge_cost_currency, vehicle.vehicles * vehicle.vehicle_charge_category_cost * vehicle.vehicle_no_of_nights * ((100 - vehicle.discount) / 100)).amount) }}</td>
                            <td>{{ vehicle.discount }}%</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <div class="table table-responsive m-t" v-if="singleBooking.facility_bookings.length > 0">
                      <h4 class="earth-color">CONSERVANCY ACCOMMODATION</h4>
                      <table class="table table-striped">
                        <thead>
                          <tr>
                            <td><b>Name</b></td>
                            <td><b>Guests</b></td>
                            <td><b>Duration</b></td>
                            <td><b>No of Nights</b></td>
                            <td><b>Amount</b></td>
                            <td><b>Discount %</b></td>
                            <td><b>Discount</b></td>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="(facility, key) in singleBooking.facility_bookings" :key="key">
                            <td>{{ facility.facility_name }}</td>
                            <td>{{ facility.facility_booking_guests }}</td>
                            <td>{{ formated_date(facility.facility_booking_check_in_date) }} - {{ formated_date(facility.facility_booking_check_out_date) }}</td>
                            <td>{{ facility.facility_no_of_nights }}</td>
                            <td>{{ facility.facility_booking_currency }} {{ $roundOff(facility.facility_booking_total_cost) }}</td>
                            <td>{{ facility.facility_booking_discount_percentage }}</td>
                            <td>{{ facility.facility_booking_currency }} {{ facility.facility_booking_discount }}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <div class="table table-responsive m-t" v-if="singleBooking.external_facility_bookings.length > 0">
                      <h4 class="earth-color">OL PEJETA ESCAPES</h4>
                      <table class="table table-striped">
                        <thead>
                          <tr>
                            <td><b>Name</b></td>
                            <td><b>Parameter Name</b></td>
                            <td><b>Value</b></td>
                            <td><b>Duration</b></td>
                            <td><b>No of Nights</b></td>
                            <td><b>Amount</b></td>
                            <td><b>Discount %</b></td>
                            <td><b>Discount</b></td>
                            <td><b>Supplement Price</b></td>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="(facility, key) in singleBooking.external_facility_bookings" :key="key">
                            <td>{{ facility.facility_name }}</td>
                            <td>{{ facility.pricing_parameter.parameter_name }}</td>
                            <td>{{ facility.pricing_parameter.parameter_default_value }}</td>
                            <td>{{ formated_date(facility.facility_booking_check_in_date) }} - {{ formated_date(facility.facility_booking_check_out_date) }}</td>
                            <td>{{ facility.facility_no_of_nights }}</td>
                            <td>{{ facility.facility_booking_currency }} {{ $roundOff(facility.facility_booking_total_cost) }}</td>
                            <td>{{ facility.facility_booking_discount_percentage }}%</td>
                            <td>{{ facility.facility_booking_currency }} {{ facility.facility_booking_discount }}</td>
                            <td>
                              <p v-for="(supPrice, key) in facility.pricing_parameter.supplement_prices" :key="key">
                                <span>{{ supPrice.supplement_price_name }} ({{ facility.facility_booking_currency }} {{ $roundOff(supPrice.supplement_fixed_rate) }})</span>
                              </p>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <div class="table table-responsive m-t" v-if="singleBooking.inventory_bookings.length > 0">
                      <h4 class="earth-color">CONSERVANCY ACTIVITIES</h4>
                      <table class="table table-striped">
                        <thead>
                          <tr>
                            <td><b>Name</b></td>
                            <td><b>Guests</b></td>
                            <td><b>Date</b></td>
                            <td><b>Amount</b></td>
                            <td><b>Discount %</b></td>
                            <td><b>Discount</b></td>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="(activity, key) in singleBooking.inventory_bookings" :key="key">
                            <td>{{ activity.inventory_name }}</td>
                            <td>{{ activity.inventory_booking_guests }}</td>
                            <td>{{ formated_date(activity.inventory_booking_date) }}</td>
                            <td>{{ activity.inventory_booking_currency }} {{ $roundOff(activity.inventory_booking_total_cost) }}</td>
                            <td>{{ activity.inventory_booking_discount_percentage }}</td>
                            <td>{{ activity.inventory_booking_currency }} {{ activity.inventory_booking_total_discount }}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <div class="row m-t">
                      <div class="col-md-4 col-md-offset-8">
                        <h4 class="earth-color">TOTAL</h4>
                        <table class="table table-striped text-right">
                          <tbody>
                            <tr v-if="singleBooking.catering_levy !== null">
                              <td><strong>Catering Levy :</strong></td>
                              <td>{{ $roundOff(singleBooking.catering_levy.tax) }}</td>
                            </tr>
                            <tr>
                              <td><strong>Sub Total :</strong></td>
                              <td>{{ $roundOff(singleBooking.vat.after_tax) }}</td>
                            </tr>
                            <tr>
                              <td><strong>VAT :</strong></td>
                              <td>{{ $roundOff(singleBooking.vat.tax) }}</td>
                            </tr>
                            <tr>
                              <td><h2 class="earth">GRAND TOTAL :</h2></td>
                              <td><h2>{{ singleBooking.currency }} {{ $roundOff(singleBooking.total_cost) }}</h2></td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </v-tab-item>
                  <v-tab-item key="guests">
                    <div class="col-md-12">
                      <v-layout row wrap>
                        <v-flex lg3 md3 sm6 xs12>
                          <v-container class="booking-summary-container" fluid>
                            <v-autocomplete label="Entry Fees" v-model="credit_note_guests.guest" :items="singleBooking.guests" :rules="[(v) => !!v || 'Item is required']" required item-text="payment_person" :return-object="true" :loading="$loadingTrue" @change="populateGuestData"></v-autocomplete>
                          </v-container>
                        </v-flex>
                        <v-flex lg2 md2 sm6 xs12>
                          <v-container class="booking-summary-container" fluid>
                            <v-text-field v-model="credit_note_guests.number_of_guests" label="Number" type="number" min="0" :max="credit_note_guests.max_guests"></v-text-field>
                          </v-container>
                        </v-flex>
                        <v-flex lg2 md2 sm6 xs12>
                          <v-container class="booking-summary-container" fluid>
                            <v-text-field v-model="credit_note_guests.guest_discount" label="Discount" type="number" min="0" max="100"></v-text-field>
                          </v-container>
                        </v-flex>
                        <v-flex lg2 md2 sm6 xs12>
                          <v-container class="booking-summary-container" fluid>
                            <v-menu v-model="credit_note_guests.check_in_date_dialog" :close-on-content-click="false" :nudge-right="40" lazy transition="scale-transition" offset-y full-width max-width="290px" min-width="290px" v-if="credit_note_guests.number_of_guests !== 0">
                              <template slot="activator">
                                <v-text-field v-model="credit_note_guests.check_in_date" label="Check-In Date" hint="YYYY/MM/DD" persistent-hint v-if="credit_note_guests.number_of_guests !== 0"></v-text-field>
                              </template>
                              <v-date-picker color="primary" v-model="credit_note_guests.check_in_date" no-title @input="credit_note_guests.check_in_date_dialog = false"></v-date-picker>
                            </v-menu>
                          </v-container>
                        </v-flex>
                        <v-flex lg2 md2 sm6 xs12>
                          <v-container class="booking-summary-container" fluid>
                            <v-menu v-model="credit_note_guests.check_out_date_dialog" :close-on-content-click="false" :nudge-right="40" lazy transition="scale-transition" offset-y full-width max-width="290px" min-width="290px" v-if="credit_note_guests.number_of_guests !== 0">
                              <template slot="activator">
                                <v-text-field v-model="credit_note_guests.check_out_date" label="Check-Out Date" hint="YYYY/MM/DD" persistent-hint v-if="credit_note_guests.number_of_guests !== 0"></v-text-field>
                              </template>
                              <v-date-picker color="primary" v-model="credit_note_guests.check_out_date" no-title @input="credit_note_guests.check_out_date_dialog = false"></v-date-picker>
                            </v-menu>
                          </v-container>
                        </v-flex>
                        <v-flex lg1 md1 sm3 xs6>
                          <v-container class="booking-summary-container" fluid>
                            <v-btn :depressed="true" color="success" v-if="credit_note_guests.number_of_guests !== 0" @click="addGuestToCreditNote">
                              <i class="fa fa-plus"></i>
                            </v-btn>
                          </v-container>
                        </v-flex>
                      </v-layout>
                      <v-layout row wrap>
                        <h5 class="earth">ENTRY FEES ADDED TO CREDIT NOTE</h5>
                        <table cellpadding="1" cellspacing="1" class="table table-striped">
                          <thead>
                            <tr>
                              <th>Entry Fees</th>
                              <th>Number of Guests</th>
                              <th>Discount</th>
                              <th>Dates</th>
                              <th>Amount</th>
                              <th></th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="(guest, key) in credit_note_details.guests" :key="key">
                              <td>{{ guest.guest.payment_person }}</td>
                              <td>{{ guest.number_of_guests }}</td>
                              <td>{{ guest.guest_discount }}%</td>
                              <td>{{ formated_date(guest.check_in_date) }} to {{ formated_date(guest.check_out_date) }}</td>
                              <td>{{ guest.guest.payment_person_currency }} {{ $roundOff((guest.guest.payment_person_amount * guest.number_of_guests) * ((100 - guest.guest_discount) / 100)) }}</td>
                              <td>
                                <v-btn :depressed="true" color="warning" @click="removeGuestFromCreditNote(key)">
                                  <i class="fa fa-times"></i>
                                </v-btn>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </v-layout>
                    </div>
                  </v-tab-item>
                  <v-tab-item key="vehicles">
                    <div class="col-md-12">
                      <v-layout row wrap>
                        <v-flex lg3 md3 sm6 xs12>
                          <v-container class="booking-summary-container" fluid>
                            <v-autocomplete label="Vehicle Fees" v-model="credit_note_vehicles.vehicle" :items="singleBooking.vehicles" :rules="[(v) => !!v || 'Item is required']" required item-text="vehicle_charge_category" :return-object="true" :loading="$loadingTrue" @change="populateVehicleData"></v-autocomplete>
                          </v-container>
                        </v-flex>
                        <v-flex lg2 md2 sm6 xs12>
                          <v-container class="booking-summary-container" fluid>
                            <v-text-field v-model="credit_note_vehicles.number_of_vehicles" label="Number" type="number" min="0" :max="credit_note_vehicles.max_vehicles"></v-text-field>
                          </v-container>
                        </v-flex>
                        <v-flex lg2 md2 sm6 xs12>
                          <v-container class="booking-summary-container" fluid>
                            <v-text-field v-model="credit_note_vehicles.vehicle_discount" label="Discount" type="number" min="0" max="100"></v-text-field>
                          </v-container>
                        </v-flex>
                        <v-flex lg3 md3 sm6 xs12>
                          <v-container class="booking-summary-container" fluid>
                            <v-menu v-model="credit_note_vehicles.check_in_date_dialog" :close-on-content-click="false" :nudge-right="40" lazy transition="scale-transition" offset-y full-width max-width="290px" min-width="290px" v-if="credit_note_vehicles.number_of_vehicles !== 0">
                              <template slot="activator">
                                <v-text-field v-model="credit_note_vehicles.check_in_date" label="Check-In Date" hint="YYYY/MM/DD" persistent-hint v-if="credit_note_vehicles.number_of_vehicles !== 0"></v-text-field>
                              </template>
                              <v-date-picker color="primary" v-model="credit_note_vehicles.check_in_date" no-title @input="credit_note_vehicles.check_in_date_dialog = false"></v-date-picker>
                            </v-menu>
                          </v-container>
                        </v-flex>
                        <v-flex lg3 md3 sm6 xs12>
                          <v-container class="booking-summary-container" fluid>
                            <v-menu v-model="credit_note_vehicles.check_out_date_dialog" :close-on-content-click="false" :nudge-right="40" lazy transition="scale-transition" offset-y full-width max-width="290px" min-width="290px" v-if="credit_note_vehicles.number_of_vehicles !== 0">
                              <template slot="activator">
                                <v-text-field v-model="credit_note_vehicles.check_out_date" label="Check-Out Date" hint="YYYY/MM/DD" persistent-hint v-if="credit_note_vehicles.number_of_vehicles !== 0"></v-text-field>
                              </template>
                              <v-date-picker color="primary" v-model="credit_note_vehicles.check_out_date" no-title @input="credit_note_vehicles.check_out_date_dialog = false"></v-date-picker>
                            </v-menu>
                          </v-container>
                        </v-flex>
                        <v-flex lg1 md1 sm3 xs6>
                          <v-container class="booking-summary-container" fluid>
                            <v-btn :depressed="true" color="success" v-if="credit_note_vehicles.number_of_vehicles !== 0" @click="addVehicleToCreditNote">
                              <i class="fa fa-plus"></i>
                            </v-btn>
                          </v-container>
                        </v-flex>
                      </v-layout>
                      <v-layout row wrap>
                        <h5 class="earth">VEHICLE FEES ADDED TO CREDIT NOTE</h5>
                        <table cellpadding="1" cellspacing="1" class="table table-striped">
                          <thead>
                            <tr>
                              <th>Vehicle Fees</th>
                              <th>Number of Vehicles</th>
                              <th>Discount</th>
                              <th>Dates</th>
                              <th>Amount</th>
                              <th></th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="(vehicle, key) in credit_note_details.vehicles" :key="key">
                              <td>{{ vehicle.vehicle.vehicle_charge_category }}</td>
                              <td>{{ vehicle.number_of_vehicles }}</td>
                              <td>{{ vehicle.vehicle_discount }}%</td>
                              <td>{{ formated_date(vehicle.check_in_date) }} to {{ formated_date(vehicle.check_out_date) }}</td>
                              <td>{{ singleBooking.currency }} {{ $roundOff((vehicle.vehicle.vehicle_charge_category_cost * vehicle.number_of_vehicles) * ((100 - vehicle.vehicle_discount) / 100)) }}</td>
                              <td>
                                <v-btn :depressed="true" color="warning" @click="removeVehicleFromCreditNote(key)">
                                  <i class="fa fa-times"></i>
                                </v-btn>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </v-layout>
                    </div>
                  </v-tab-item>
                  <v-tab-item key="accommodations">
                    <div class="col-md-12">
                      <v-layout row wrap>
                        <v-flex lg3 md3 sm6 xs12>
                          <v-container class="booking-summary-container" fluid>
                            <v-autocomplete label="Accommodation" v-model="credit_note_accommodations.accommodation" :items="singleBooking.facility_bookings" :rules="[v => !!v || 'Item is required']" required item-text="facility_name" item-value="facility_booking_public_id" :return-object="true" :loading="$loadingTrue" @change="populateAccommodationData"></v-autocomplete>
                          </v-container>
                        </v-flex>
                        <v-flex lg3 md3 sm6 xs12>
                          <v-container class="booking-summary-container" fluid>
                            <v-text-field v-model="credit_note_accommodations.number_of_adults" label="Adults" type="number" min="0" :max="credit_note_accommodations.max_adults"></v-text-field>
                          </v-container>
                        </v-flex>
                        <v-flex lg3 md3 sm6 xs12>
                          <v-container class="booking-summary-container" fluid>
                            <v-text-field v-model="credit_note_accommodations.number_of_children" label="Children" type="number" min="0" :max="credit_note_accommodations.max_children"></v-text-field>
                          </v-container>
                        </v-flex>
                        <v-flex lg3 md3 sm6 xs12>
                          <v-container class="booking-summary-container" fluid>
                            <v-text-field v-model="credit_note_accommodations.number_of_extra_adults" label="Extra Adults" type="number" min="0" :max="credit_note_accommodations.max_extra_adults"></v-text-field>
                          </v-container>
                        </v-flex>
                        <v-flex lg3 md3 sm6 xs12>
                          <v-container class="booking-summary-container" fluid>
                            <v-text-field v-model="credit_note_accommodations.number_of_extra_children" label="Extra Children" type="number" min="0" :max="credit_note_accommodations.max_extra_children"></v-text-field>
                          </v-container>
                        </v-flex>
                        <v-flex lg3 md3 sm6 xs12>
                          <v-container class="booking-summary-container" fluid>
                            <v-text-field v-model="credit_note_accommodations.discount" label="Discount" type="number" min="0" max="100"></v-text-field>
                          </v-container>
                        </v-flex>
                        <v-flex lg3 md3 sm6 xs12>
                          <v-container class="booking-summary-container" fluid>
                            <v-menu v-model="credit_note_accommodations.check_in_date_dialog" :close-on-content-click="false" :nudge-right="40" lazy transition="scale-transition" offset-y full-width max-width="290px" min-width="290px" v-if="credit_note_accommodations.number_of_adults + credit_note_accommodations.number_of_children + credit_note_accommodations.number_of_extra_adults + credit_note_accommodations.number_of_extra_children !== 0">
                              <template slot="activator">
                                <v-text-field v-model="credit_note_accommodations.check_in_date" label="Check-In Date" hint="YYYY/MM/DD" persistent-hint v-if="credit_note_accommodations.number_of_adults + credit_note_accommodations.number_of_children + credit_note_accommodations.number_of_extra_adults + credit_note_accommodations.number_of_extra_children !== 0"></v-text-field>
                              </template>
                              <v-date-picker color="primary" v-model="credit_note_accommodations.check_in_date" no-title @input="credit_note_accommodations.check_in_date_dialog = false"></v-date-picker>
                            </v-menu>
                          </v-container>
                        </v-flex>
                        <v-flex lg3 md3 sm6 xs12>
                          <v-container class="booking-summary-container" fluid>
                            <v-menu v-model="credit_note_accommodations.check_out_date_dialog" :close-on-content-click="false" :nudge-right="40" lazy transition="scale-transition" offset-y full-width max-width="290px" min-width="290px" v-if="credit_note_accommodations.number_of_adults + credit_note_accommodations.number_of_children + credit_note_accommodations.number_of_extra_adults + credit_note_accommodations.number_of_extra_children !== 0">
                              <template slot="activator">
                                <v-text-field v-model="credit_note_accommodations.check_out_date" label="Check-Out Date" hint="YYYY/MM/DD" persistent-hint v-if="credit_note_accommodations.number_of_adults + credit_note_accommodations.number_of_children + credit_note_accommodations.number_of_extra_adults + credit_note_accommodations.number_of_extra_children !== 0"></v-text-field>
                              </template>
                              <v-date-picker color="primary" v-model="credit_note_accommodations.check_out_date" no-title @input="credit_note_accommodations.check_out_date_dialog = false"></v-date-picker>
                            </v-menu>
                          </v-container>
                        </v-flex>
                        <v-flex lg1 md1 sm3 xs6>
                          <v-container class="booking-summary-container" fluid>
                            <v-btn :depressed="true" color="success" v-if="credit_note_accommodations.number_of_adults + credit_note_accommodations.number_of_children + credit_note_accommodations.number_of_extra_adults + credit_note_accommodations.number_of_extra_children !== 0" @click="addAccommodationToCreditNote">
                              <i class="fa fa-plus"></i>
                            </v-btn>
                          </v-container>
                        </v-flex>
                      </v-layout>
                      <v-layout row wrap>
                        <h5 class="earth">ACCOMMODATION ADDED TO CREDIT NOTE</h5>
                        <table cellpadding="1" cellspacing="1" class="table table-striped">
                          <thead>
                            <tr>
                              <th>Accommodation</th>
                              <th>Adults</th>
                              <th>Children</th>
                              <th>Extra Adults</th>
                              <th>Extra Children</th>
                              <th>Discount</th>
                              <th>Dates</th>
                              <th>Amount</th>
                              <th></th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="(accommodation, key) in credit_note_details.accommodations" :key="key">
                              <td>{{ accommodation.accommodation.facility_name }}</td>
                              <td>{{ accommodation.number_of_adults }}</td>
                              <td>{{ accommodation.number_of_children }}</td>
                              <td>{{ accommodation.number_of_extra_adults }}</td>
                              <td>{{ accommodation.number_of_extra_children }}</td>
                              <td>{{ accommodation.discount }}%</td>
                              <td>{{ formated_date(accommodation.check_in_date) }} to {{ formated_date(accommodation.check_out_date) }}</td>
                              <td>{{ accommodation.accommodation.facility_booking_currency }} {{ $roundOff(accommodation.accommodation.facility_booking_total_cost * ((100 - accommodation.discount) / 100)) }}</td>
                              <td>
                                <v-btn :depressed="true" color="warning" @click="removeAccommodationFromCreditNote(key)">
                                  <i class="fa fa-times"></i>
                                </v-btn>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </v-layout>
                    </div>
                  </v-tab-item>
                  <v-tab-item key="external_accommodations">
                    <div class="col-md-12">
                      <v-layout row wrap>
                        <v-flex lg4 md4 sm6 xs12>
                          <v-container class="booking-summary-container" fluid>
                            <v-autocomplete label="Accommodation" v-model="credit_note_external_accommodation.facility_booking_public_id" :items="singleBooking.external_facility_bookings" :rules="[v => !!v || 'Item is required']" required item-text="facility_name" item-value="facility_booking_public_id" :return-object="true" :loading="$loadingTrue" @change="populateExternalAccommodationData"></v-autocomplete>
                          </v-container>
                        </v-flex>
                        <v-flex lg4 md4 sm6 xs12>
                          <v-container class="booking-summary-container" fluid>
                            <v-text-field v-model="credit_note_external_accommodation.parameter_default_value" label="Value" type="number" min="0"></v-text-field>
                          </v-container>
                        </v-flex>
                        <v-flex lg4 md4 sm6 xs12>
                          <v-container class="booking-summary-container" fluid>
                            <v-text-field v-model="credit_note_external_accommodation.parameter_name" label="Parameter Name"></v-text-field>
                          </v-container>
                        </v-flex>
                        <v-flex lg4 md4 sm6 xs12>
                          <v-container class="booking-summary-container" fluid>
                            <v-text-field v-model="credit_note_external_accommodation.discount" label="Discount" type="number" min="0" max="100"></v-text-field>
                          </v-container>
                        </v-flex>
                        <v-flex lg3 md3 sm6 xs12>
                          <v-container class="booking-summary-container" fluid>
                            <v-menu v-model="credit_note_external_accommodation.external_check_in_date_dialog" :close-on-content-click="false" :nudge-right="40" lazy transition="scale-transition" offset-y full-width max-width="290px" min-width="290px" v-if="credit_note_external_accommodation.facility_booking_public_id != ''">
                              <template slot="activator">
                                <v-text-field v-model="credit_note_external_accommodation.check_in_date" label="Check-In Date" hint="YYYY/MM/DD" persistent-hint v-if="credit_note_external_accommodation.facility_booking_public_id != ''"></v-text-field>
                              </template>
                              <v-date-picker color="primary" v-model="credit_note_external_accommodation.check_in_date" no-title @input="credit_note_external_accommodation.external_check_in_date_dialog = false"></v-date-picker>
                            </v-menu>
                          </v-container>
                        </v-flex>
                        <v-flex lg3 md3 sm6 xs12>
                          <v-container class="booking-summary-container" fluid>
                            <v-menu v-model="credit_note_external_accommodation.external_check_out_date_dialog" :close-on-content-click="false" :nudge-right="40" lazy transition="scale-transition" offset-y full-width max-width="290px" min-width="290px" v-if="credit_note_external_accommodation.facility_booking_public_id != ''">
                              <template slot="activator">
                                <v-text-field v-model="credit_note_external_accommodation.check_out_date" label="Check-Out Date" hint="YYYY/MM/DD" persistent-hint v-if="credit_note_external_accommodation.facility_booking_public_id != ''"></v-text-field>
                              </template>
                              <v-date-picker color="primary" v-model="credit_note_external_accommodation.check_out_date" no-title @input="credit_note_external_accommodation.external_check_out_date_dialog = false"></v-date-picker>
                            </v-menu>
                          </v-container>
                        </v-flex>
                        <v-flex lg1 md1 sm3 xs6>
                          <v-container class="booking-summary-container" fluid>
                            <v-btn :depressed="true" color="success" @click="addExternalAccommodationToCreditNote">
                              <i class="fa fa-plus"></i>
                            </v-btn>
                          </v-container>
                        </v-flex>
                      </v-layout>
                      <v-layout row wrap>
                        <h5 class="earth">EXTERNAL ACCOMMODATION ADDED TO CREDIT NOTE</h5>
                        <table cellpadding="1" cellspacing="1" class="table table-striped">
                          <thead>
                            <tr>
                              <th>Accommodation</th>
                              <th>Parameter Name</th>
                              <th>Value</th>
                              <th>Discount</th>
                              <th>Supplement Price</th>
                              <th>Dates</th>
                              <th>Amount</th>
                              <th></th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="(external_accommodation, key) in credit_note_details.external_accommodations" :key="key">
                              <td>{{ external_accommodation.facility_name }}</td>
                              <td>{{ external_accommodation.parameter_name }}</td>
                              <td>{{ external_accommodation.parameter_default_value }}</td>
                              <td>{{ external_accommodation.discount }}%</td>
                              <td>
                                <p v-for="(supPrice, key) in external_accommodation.supplement_price" :key="key">
                                  <span>{{ supPrice.supplement_price_name }}</span>
                                </p>
                              </td>
                              <td>{{ formated_date(external_accommodation.check_in_date) }} to {{ formated_date(external_accommodation.check_out_date) }}</td>
                              <td>{{ singleBooking.currency }} {{ $roundOff(external_accommodation.facility_booking_total_cost * ((100 - external_accommodation.discount) / 100)) }}</td>
                              <td>
                                <v-btn :depressed="true" color="warning" @click="removeExternalAccommodationFromCreditNote(key)">
                                  <i class="fa fa-times"></i>
                                </v-btn>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </v-layout>
                    </div>
                  </v-tab-item>
                  <v-tab-item key="activities">
                    <div class="col-md-12">
                      <v-layout row wrap>
                        <v-flex lg3 md3 sm6 xs12>
                          <v-container class="booking-summary-container" fluid>
                            <v-autocomplete label="Activity" v-model="credit_note_activities.activity" :items="singleBooking.inventory_bookings" :rules="[(v) => !!v || 'Item is required']" required item-text="inventory_name" :return-object="true" :loading="$loadingTrue" @change="populateActivityData"></v-autocomplete>
                          </v-container>
                        </v-flex>
                        <v-flex lg3 md3 sm6 xs12>
                          <v-container class="booking-summary-container" fluid>
                            <v-text-field v-model="credit_note_activities.number_of_adults" label="Adults" type="number" min="0" :max="credit_note_activities.max_adults"></v-text-field>
                          </v-container>
                        </v-flex>
                        <v-flex lg3 md3 sm6 xs12>
                          <v-container class="booking-summary-container" fluid>
                            <v-text-field v-model="credit_note_activities.number_of_children" label="Children" type="number" min="0" :max="credit_note_activities.max_children"></v-text-field>
                          </v-container>
                        </v-flex>
                        <v-flex lg3 md3 sm6 xs12>
                          <v-container class="booking-summary-container" fluid>
                            <v-text-field v-model="credit_note_activities.number_of_extra_adults" label="Extra Adults" type="number" min="0" :max="credit_note_activities.max_extra_adults"></v-text-field>
                          </v-container>
                        </v-flex>
                        <v-flex lg3 md3 sm6 xs12>
                          <v-container class="booking-summary-container" fluid>
                            <v-text-field v-model="credit_note_activities.number_of_extra_children" label="Extra Children" type="number" min="0" :max="credit_note_activities.max_extra_children"></v-text-field>
                          </v-container>
                        </v-flex>
                        <v-flex lg3 md3 sm6 xs12>
                          <v-container class="booking-summary-container" fluid>
                            <v-text-field v-model="credit_note_activities.discount" label="Discount" type="number" min="0" max="100"></v-text-field>
                          </v-container>
                        </v-flex>
                        <v-flex lg3 md3 sm6 xs12>
                          <v-container class="booking-summary-container" fluid>
                            <v-menu v-model="credit_note_activities.activity_date_dialog" :close-on-content-click="false" :nudge-right="40" lazy transition="scale-transition" offset-y full-width max-width="290px" min-width="290px" v-if="credit_note_activities.number_of_adults + credit_note_activities.number_of_children + credit_note_activities.number_of_extra_adults + credit_note_activities.number_of_extra_children !== 0">
                              <template slot="activator">
                                <v-text-field v-model="credit_note_activities.activity_date" label="Activity Date" hint="YYYY/MM/DD" persistent-hint v-if="credit_note_activities.number_of_adults + credit_note_activities.number_of_children + credit_note_activities.number_of_extra_adults + credit_note_activities.number_of_extra_children !== 0"></v-text-field>
                              </template>
                              <v-date-picker color="primary" v-model="credit_note_activities.activity_date" no-title @input="credit_note_activities.activity_date_dialog = false"></v-date-picker>
                            </v-menu>
                          </v-container>
                        </v-flex>
                        <v-flex lg1 md1 sm3 xs6>
                          <v-container class="booking-summary-container" fluid>
                            <v-btn :depressed="true" color="success" v-if="credit_note_activities.number_of_adults + credit_note_activities.number_of_children + credit_note_activities.number_of_extra_adults + credit_note_activities.number_of_extra_children !== 0" @click="addActivityToCreditNote">
                              <i class="fa fa-plus"></i>
                            </v-btn>
                          </v-container>
                        </v-flex>
                      </v-layout>
                      <v-layout row wrap>
                        <h5 class="earth">ACTIVITIES ADDED TO CREDIT NOTE</h5>
                        <table cellpadding="1" cellspacing="1" class="table table-striped">
                          <thead>
                            <tr>
                              <th>Activity</th>
                              <th>Adults</th>
                              <th>Children</th>
                              <th>Extra Adults</th>
                              <th>Extra Children</th>
                              <th>Discount</th>
                              <th>Date</th>
                              <th>Amount</th>
                              <th></th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="(activity, key) in credit_note_details.activities" :key="key">
                              <td>{{ activity.activity.inventory_name }}</td>
                              <td>{{ activity.number_of_adults }}</td>
                              <td>{{ activity.number_of_children }}</td>
                              <td>{{ activity.number_of_extra_adults }}</td>
                              <td>{{ activity.number_of_extra_children }}</td>
                              <td>{{ activity.discount }}%</td>
                              <td>{{ formated_date(activity.activity_date) }}</td>
                              <td>{{ activity.activity.inventory_booking_currency }} {{ $roundOff(activity.activity.inventory_booking_total_cost * ((100 - activity.discount) / 100)) }}</td>
                              <td>
                                <v-btn :depressed="true" color="warning" @click="removeActivityFromCreditNote(key)">
                                  <i class="fa fa-times"></i>
                                </v-btn>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </v-layout>
                    </div>
                  </v-tab-item>
                  <v-tab-item key="summary">
                    <div class="col-md-12">
                      <v-layout row wrap>
                        <h5 class="earth">ENTRY FEES</h5>
                        <table cellpadding="1" cellspacing="1" class="table table-striped">
                          <thead>
                            <tr>
                              <th>Entry Fees</th>
                              <th>Number of Guests</th>
                              <th>Dates</th>
                              <th>Amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="(guest, key) in credit_note_details.guests" :key="key">
                              <td>{{ guest.guest.payment_person }}</td>
                              <td>{{ guest.number_of_guests }}</td>
                              <td>{{ formated_date(guest.check_in_date) }} to {{ formated_date(guest.check_out_date) }}</td>
                              <td>{{ guest.guest.payment_person_currency }} {{ $roundOff((guest.guest.payment_person_amount * guest.number_of_guests) * ((100 - guest.guest_discount) / 100)) }}</td>
                            </tr>
                          </tbody>
                        </table>
                      </v-layout>
                      <v-layout row wrap>
                        <h5 class="earth">VEHICLE FEES</h5>
                        <table cellpadding="1" cellspacing="1" class="table table-striped">
                          <thead>
                            <tr>
                              <th>Vehicle Fees</th>
                              <th>Number of Vehicles</th>
                              <th>Dates</th>
                              <th>Amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="(vehicle, key) in credit_note_details.vehicles" :key="key">
                              <td>{{ vehicle.vehicle.vehicle_charge_category }}</td>
                              <td>{{ vehicle.number_of_vehicles }}</td>
                              <td>{{ formated_date(vehicle.check_in_date) }} to {{ formated_date(vehicle.check_out_date) }}</td>
                              <td>{{ singleBooking.currency }} {{ $roundOff((vehicle.vehicle.vehicle_charge_category_cost * vehicle.number_of_vehicles) * ((100 - vehicle.vehicle_discount) / 100)) }}</td>
                            </tr>
                          </tbody>
                        </table>
                      </v-layout>
                      <v-layout row wrap>
                        <h5 class="earth">ACCOMMODATION</h5>
                        <table cellpadding="1" cellspacing="1" class="table table-striped">
                          <thead>
                            <tr>
                              <th>Accommodation</th>
                              <th>Adults</th>
                              <th>Children</th>
                              <th>Extra Adults</th>
                              <th>Extra Children</th>
                              <th>Dates</th>
                              <th>Amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="(accommodation, key) in credit_note_details.accommodations" :key="key">
                              <td>{{ accommodation.accommodation.facility_name }}</td>
                              <td>{{ accommodation.number_of_adults }}</td>
                              <td>{{ accommodation.number_of_children }}</td>
                              <td>{{ accommodation.number_of_extra_adults }}</td>
                              <td>{{ accommodation.number_of_extra_children }}</td>
                              <td>{{ formated_date(accommodation.check_in_date) }} to {{ formated_date(accommodation.check_out_date) }}</td>
                              <td>{{ accommodation.accommodation.facility_booking_currency }} {{ $roundOff(accommodation.accommodation.facility_booking_total_cost * ((100 - accommodation.discount) / 100)) }}</td>
                            </tr>
                          </tbody>
                        </table>
                      </v-layout>
                      <v-layout row wrap>
                        <h5 class="earth">OL PEJETA ESCAPES ACCOMMODATION</h5>
                        <table cellpadding="1" cellspacing="1" class="table table-striped">
                          <thead>
                            <tr>
                              <th>Accommodation</th>
                              <th>Parameter Name</th>
                              <th>Value</th>
                              <th>Discount</th>
                              <th>Supplement Price</th>
                              <th>Dates</th>
                              <th>Amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="(external_accommodation, key) in credit_note_details.external_accommodations" :key="key">
                              <td>{{ external_accommodation.facility_name }}</td>
                              <td>{{ external_accommodation.parameter_name }}</td>
                              <td>{{ external_accommodation.parameter_default_value }}</td>
                              <td>{{ external_accommodation.discount }}%</td>
                              <td>
                                <p v-for="(supPrice, key) in external_accommodation.supplement_price" :key="key">
                                  <span>{{ supPrice.supplement_price_name }}</span>
                                </p>
                              </td>
                              <td>{{ formated_date(external_accommodation.check_in_date) }} to {{ formated_date(external_accommodation.check_out_date) }}</td>
                              <td>{{ singleBooking.currency }} {{ $roundOff(external_accommodation.facility_booking_total_cost * ((100 - external_accommodation.discount) / 100)) }}</td>
                            </tr>
                          </tbody>
                        </table>
                      </v-layout>
                      <v-layout row wrap>
                        <h5 class="earth">ACTIVITIES</h5>
                        <table cellpadding="1" cellspacing="1" class="table table-striped">
                          <thead>
                            <tr>
                              <th>Activity</th>
                              <th>Adults</th>
                              <th>Children</th>
                              <th>Extra Adults</th>
                              <th>Extra Children</th>
                              <th>Date</th>
                              <th>Amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="(activity, key) in credit_note_details.activities" :key="key">
                              <td>{{ activity.activity.inventory_name }}</td>
                              <td>{{ activity.number_of_adults }}</td>
                              <td>{{ activity.number_of_children }}</td>
                              <td>{{ activity.number_of_extra_adults }}</td>
                              <td>{{ activity.number_of_extra_children }}</td>
                              <td>{{ formated_date(activity.activity_date) }}</td>
                              <td>{{ activity.activity.inventory_booking_currency }} {{ $roundOff(activity.activity.inventory_booking_total_cost * ((100 - activity.discount) / 100)) }}</td>
                            </tr>
                          </tbody>
                        </table>
                      </v-layout>
                      <v-layout row wrap>
                        <h5 class="earth">TOTAL CREDIT NOTE AMOUNT</h5>
                        <table class="table table-striped text-right">
                          <tbody>
                            <tr>
                              <td><strong>Total Amount:</strong></td>
                              <td>
                                {{ singleBooking.currency }} {{
                                  $roundOff(
                                    credit_note_details.guests.reduce((sum, guest) => sum + ((guest.guest.payment_person_amount * guest.number_of_guests) * ((100 - guest.guest_discount) / 100)), 0) +
                                    credit_note_details.vehicles.reduce((sum, vehicle) => sum + ((vehicle.vehicle.vehicle_charge_category_cost * vehicle.number_of_vehicles) * ((100 - vehicle.vehicle_discount) / 100)), 0) +
                                    credit_note_details.accommodations.reduce((sum, accommodation) => sum + (accommodation.accommodation.facility_booking_total_cost * ((100 - accommodation.discount) / 100)), 0) +
                                    credit_note_details.external_accommodations.reduce((sum, external) => sum + (external.facility_booking_total_cost * ((100 - external.discount) / 100)), 0) +
                                    credit_note_details.activities.reduce((sum, activity) => sum + (activity.activity.inventory_booking_total_cost * ((100 - activity.discount) / 100)), 0)
                                  )
                                }}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </v-layout>
                    </div>
                    <div class="col-md-3">
                      <v-layout row wrap>
                        <v-textarea v-model="credit_note_details.reason" box label="Reason"></v-textarea>
                      </v-layout>
                    </div>
                    <div class="col-md-3">
                      <v-layout row wrap>
                        <v-btn :depressed="true" color="success" @click="postCreditNote" v-if="credit_note_details.reason !== ''">
                          Issue Credit Note
                        </v-btn>
                      </v-layout>
                    </div>
                  </v-tab-item>
                </v-tabs>
              </v-container>
            </v-layout>
          </v-list>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="imageGalleryDialog" max-width="900px" persistent>
      <v-card>
        <v-card-title class="headline" style="background: #74a533; color: white;">
          <v-icon left dark>mdi-image-multiple</v-icon>
          Verification Images
          <v-spacer></v-spacer>
          <span class="white--text caption mr-3">{{ galleryImages.length }} image(s)</span>
          <v-btn v-if="galleryImages.length > 1"
                 small
                 color="white"
                 class="mr-2"
                 @click="downloadAllImages">
            <v-icon left small>mdi-download</v-icon>
            Download All
          </v-btn>
          <v-btn icon dark @click="imageGalleryDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pa-4">
          <div v-if="galleryImages.length > 0" class="image-gallery">
            <v-layout row wrap>
              <v-flex v-for="(image, index) in galleryImages"
                      :key="index"
                      lg4 md4 sm6 xs12
                      class="pa-2">
                <v-card class="gallery-card" elevation="2" @click="openFullImage(image)">
                  <div class="gallery-thumbnail-wrapper">
                    <img :src="getImageSrc(image)"
                         class="gallery-thumbnail"
                         :alt="'Verification Image ' + (index + 1)" />
                    <div class="gallery-overlay">
                      <v-icon color="white" size="32">mdi-magnify-plus</v-icon>
                    </div>
                  </div>
                  <div class="gallery-meta pa-2">
                    <v-chip small color="info" text-color="white">
                      {{ image.proof_point }}
                    </v-chip>
                    <span class="caption grey--text ml-2">
                      {{ formated_date_time(image.created_at) }}
                    </span>
                  </div>
                </v-card>
              </v-flex>
            </v-layout>
          </div>
          <div v-else class="text-center pa-4">
            <v-icon size="48" color="grey">mdi-image-off</v-icon>
            <p class="grey--text mt-2">No verification images available</p>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="imageDialog" max-width="800px" persistent>
      <v-card>
        <v-card-title class="headline" style="background: #74a533; color: white;">
          <v-icon left dark>mdi-image</v-icon>
          Verification Image
          <v-spacer></v-spacer>
          <v-btn icon dark @click="imageDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pa-4">
          <div v-if="viewingImage" class="image-container">
            <div class="image-meta mb-3">
              <v-chip small color="primary" text-color="white" class="mr-2">
                {{ viewingImage.first_name }} {{ viewingImage.last_name }}
              </v-chip>
              <v-chip small color="info" text-color="white" class="mr-2">
                {{ viewingImage.proof_point }}
              </v-chip>
              <v-chip small color="grey" text-color="white">
                {{ formated_date_time(viewingImage.created_at) }}
              </v-chip>
            </div>
            <div class="image-wrapper">
              <img :src="getImageSrc(viewingImage)"
                   class="verification-image"
                   alt="Verification Image"
                   @click="viewingImageDialog = true" />
            </div>
            <div class="image-actions mt-3 text-center">
              <v-btn small color="primary" @click="downloadImage(viewingImage)">
                <v-icon left small>mdi-download</v-icon>
                Download
              </v-btn>
              <v-btn small color="success" @click="openFullImage(viewingImage)">
                <v-icon left small>mdi-open-in-new</v-icon>
                Full Screen
              </v-btn>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="viewingImageDialog" fullscreen hide-overlay transition="dialog-bottom-transition">
      <v-card>
        <v-toolbar dark color="black">
          <v-btn icon dark @click="viewingImageDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <v-toolbar-title>
            <span v-if="viewingImage">{{ viewingImage.first_name }} {{ viewingImage.last_name }} - Verification Image</span>
            <span v-else>Verification Image</span>
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="viewPreviousImage" v-if="currentGalleryIndex > 0">
            <v-icon>mdi-chevron-left</v-icon>
          </v-btn>
          <span class="white--text caption mx-2" v-if="galleryImages.length > 0">
            {{ currentGalleryIndex + 1 }} / {{ galleryImages.length }}
          </span>
          <v-btn icon dark @click="viewNextImage" v-if="currentGalleryIndex < galleryImages.length - 1">
            <v-icon>mdi-chevron-right</v-icon>
          </v-btn>
          <v-btn icon dark @click="downloadImage(viewingImage)">
            <v-icon>mdi-download</v-icon>
          </v-btn>
        </v-toolbar>
        <div class="full-image-container">
          <img v-if="viewingImage"
               :src="getImageSrc(viewingImage)"
               class="full-verification-image"
               :alt="'Verification Image ' + (currentGalleryIndex + 1)" />
        </div>
      </v-card>
    </v-dialog>

    <v-footer app fixed height="auto" class="action-footer">
      <v-container fluid>
        <v-layout row wrap align-center justify-center>
          <v-flex shrink v-if="localData.user_type_id !== '35fcfdaf' && localData.user_type_id !== 'acc1886a' && $creditNote === true">
            <v-btn depressed class="earth-color arial-text print-badge grey-badge black--text mx-1" @click="print_credit_note_dialog = true">
              PRINT CREDIT NOTE
            </v-btn>
            <v-btn depressed class="earth-color arial-text print-badge grey-badge black--text mx-1" @click="credit_dialog = true">
              ISSUE CREDIT NOTE
            </v-btn>
          </v-flex>
          <v-flex shrink>
            <v-btn depressed class="earth-color arial-text print-badge grey-badge black--text mx-1" @click="dialog11 = true; populateCurrencyValue()">
              PRINT INVOICE
            </v-btn>
          </v-flex>
          <v-flex shrink>
            <v-btn depressed class="earth-color arial-text print-badge grey-badge black--text mx-1" @click="dialog = true; populateEmailValue()">
              SEND EMAIL
            </v-btn>
          </v-flex>
          <v-flex shrink>
            <v-btn depressed color="info" @click="openGuestPayment()" v-if="singleBooking.booking_payment_balance > 0 && singleBooking.status !== 'Cancelled' && localData.user_type_id === 'acc1886a'" class="mx-1 arial-text">
              ADD PAYMENT
            </v-btn>
          </v-flex>
          <v-flex shrink v-if="localData.user_type_id !== '35fcfdaf' && localData.user_type_id !== 'acc1886a'">
            <v-btn depressed color="error" dark @click="openBooking()" v-if="is_past_booking == 'False' && singleBooking.status !== 'Cancelled' && $update === true && singleBooking.status !== 'Checked Out' && singleBooking.status !== 'Checked In' && singleBooking.booking_payment_status !== 'Paid'" class="mx-1 arial-text">
              UPDATE
            </v-btn>
            <v-btn depressed color="info" @click="openPostponeBooking()" v-if="singleBooking.status !== 'Unconfirmed' && singleBooking.status !== 'Cancelled' && singleBooking.status !== 'Checked In' && singleBooking.status !== 'Checked Out' && singleBooking.status !== 'Abandoned' && singleBooking.status !== 'NO_SHOW' && is_past_booking == 'False' && singleBooking.booking_payment_status !== 'Not Paid'" class="mx-1 arial-text">
              POSTPONE
            </v-btn>
            <v-btn depressed color="info" @click="openPayment()" v-if="singleBooking.booking_payment_balance > 0 && singleBooking.status !== 'Cancelled'" class="mx-1 arial-text">
              ADD PAYMENT
            </v-btn>
            <v-btn depressed color="success" @click="sendReminder()" v-if="$bookingPaidStatus === false && singleBooking.booking_type !== 'Partner Booking' && singleBooking.status !== 'Cancelled'" class="mx-1 arial-text">
              SEND PAYMENT REMINDER
            </v-btn>
            <v-btn depressed color="info" @click="checkIn()" v-if="singleBooking.status !== 'Cancelled' && singleBooking.actual_booking_check_in_date === null" class="mx-1 arial-text">
              CHECK IN
            </v-btn>
            <v-btn depressed color="info" @click="checkOut()" v-if="singleBooking.actual_booking_check_out_date === null && singleBooking.actual_booking_check_in_date !== null" class="mx-1 arial-text">
              CHECK OUT
            </v-btn>
            <v-btn depressed color="warning" @click="canceling = true"
                   v-if="(singleBooking.status !== 'Cancelled' && singleBooking.status !== 'NO_SHOW' && singleBooking.status !== 'Checked Out' && canceling === false && is_past_booking == 'False' && singleBooking.actual_booking_check_out_date === null)"
                   class="mr-2 mb-2 arial-text action-btn">
              CANCEL BOOKING
            </v-btn>
            <v-btn depressed color="error" @click="cancelBooking()" v-if="singleBooking.status !== 'Cancelled' && canceling === true" class="mx-1 arial-text">
              CONFIRM CANCELLATION
            </v-btn>
            <v-btn depressed @click="deferPayment()" v-if="singleBooking.booking_type !== 'Partner Booking' && singleBooking.booking_payment_balance > 0 && singleBooking.status !== 'Cancelled'" class="mx-1 arial-text">
              TO INVOICE
            </v-btn>
            <v-btn depressed color="warning" @click="openJavaApplication()" v-if="singleBooking.status === 'Checked In'" :loading="$loadingTrue" class="mx-1 arial-text">
              PRINT RECEIPT
            </v-btn>
            <v-btn depressed color="success" @click="reinstateBooking()" v-if="singleBooking.status === 'Cancelled' && is_past_booking == 'False'" class="mx-1 arial-text">
              REINSTATE BOOKING
            </v-btn>
          </v-flex>
        </v-layout>
      </v-container>
    </v-footer>

    <v-dialog v-model="dialog11" persistent max-width="600px">
      <v-card flat class="summary-card white-card">
        <v-card-title class="knockout-header black--text">
          SELECT CURRENCY TO PRINT IN
        </v-card-title>
        <hr />
        <v-card-text>
          <v-flex xs12>
            <v-select label="Invoice Currency"
                      :items="$allCurrencies"
                      item-text="currency_name"
                      item-value="currency_public_id"
                      v-model="currency_id"
                      outline
                      class="arial-text"></v-select>
          </v-flex>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn color="red darken-1"
                 flat
                 class="arial-text black--text action-btn"
                 @click="dialog11 = false"
                 style="letter-spacing: 1px;">
            CLOSE
          </v-btn>
          <v-btn color="green darken-1"
                 flat
                 class="arial-text black--text action-btn"
                 @click="printReceipt"
                 style="letter-spacing: 1px;">
            PRINT
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialog" persistent max-width="600px">
      <v-card>
        <v-card-title>
          <h4 class="earth-color">ADD EMAILS</h4>
          <hr />
        </v-card-title>
        <v-card-text>
          <v-container class="booking-summary-container" grid-list-md>
            <v-flex xs12>
              <v-text-field label="Email*"
                            required
                            :value="singleBooking.booking_details.email_address"
                            v-model="emails.email"
                            placeholder="john@apple.com, obts@olpejetaconservancy.org"
                            outline
                            autocapitalize="none"
                            oninput="this.value=this.value.toLowerCase()"></v-text-field>
              <p class="text-danger">
                * Separate the emails with a comma
              </p>
            </v-flex>
            <v-flex xs12>
              <v-select label="Invoice Currency"
                        :items="$allCurrencies"
                        item-text="currency_name"
                        item-value="currency_public_id"
                        v-model="currency_id"
                        outline></v-select>
            </v-flex>
            <v-flex xs12>
              <v-textarea label="Summary (Message)"
                          required
                          v-model="message"
                          placeholder=""
                          auto-grow
                          outline></v-textarea>
            </v-flex>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="red darken-1" flat @click="dialog = false">Close</v-btn>
          <v-btn color="green darken-1" flat @click="sendMail()">Send</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </v-container>
</template>

<script>
  import router from "../../../router";
  import axios from "axios";

  export default {
    components: {},
    data() {
      return {
        active: null,
        localData: JSON.parse(localStorage.getItem("unem")),
        canceling: false,
        dialog: false,
        dialog1: false,
        dialog11: false,
        credit_dialog: false,
        print_credit_note_dialog: false,
        edit_activity_date_dialog: false,
        print_credit_note: {
          credit_note_id: "",
          booking_id: "",
          session_id: "",
        },
        edit_activity_date_data: {
          date_dialog: false,
          activity_date: "",
          activity_name: "",
          inventory_booking_public_id: "",
          old_activity_date: "",
          delete_count: 3,
        },
        edit_accommodation_dates_dialog: false,
        edit_accommodation_dates_data: {
          check_in_date_dialog: false,
          check_out_date_dialog: false,
          check_in_date: "",
          check_out_date: "",
          accommodation_name: "",
          accommodation_booking_public_id: "",
          old_check_in_date: "",
          old_check_out_date: "",
          delete_count: 3,
        },
        credit_note_details: {
          guests: [],
          vehicles: [],
          activities: [],
          accommodations: [],
          external_accommodations: [],
          reason: "",
          currency: "",
          booking_id: "",
        },
        credit_note_guests: {
          guest: {},
          number_of_guests: 0,
          max_guests: 0,
          guest_discount: 0,
          check_in_date_dialog: false,
          check_out_date_dialog: false,
          check_in_date: this.$moment().format("YYYY-MM-DD"),
          check_out_date: this.$moment().format("YYYY-MM-DD"),
        },
        credit_note_vehicles: {
          vehicle: {},
          number_of_vehicles: 0,
          max_vehicles: 0,
          vehicle_discount: 0,
          check_in_date_dialog: false,
          check_out_date_dialog: false,
          check_in_date: this.$moment().format("YYYY-MM-DD"),
          check_out_date: this.$moment().format("YYYY-MM-DD"),
        },
        credit_note_accommodations: {
          accommodation: {},
          discount: 0,
          number_of_adults: 0,
          max_adults: 0,
          number_of_children: 0,
          max_children: 0,
          number_of_extra_adults: 0,
          max_extra_adults: 0,
          number_of_extra_children: 0,
          max_extra_children: 0,
          check_in_date_dialog: false,
          check_out_date_dialog: false,
          check_in_date: this.$moment().format("YYYY-MM-DD"),
          check_out_date: this.$moment().format("YYYY-MM-DD"),
          parameter_default_value: 0,
          parameter_name: "",
          parameter_id: 0,
        },
        credit_note_external_accommodation: {
          facility_booking_public_id: "",
          discount: 0,
          external_check_in_date_dialog: false,
          external_check_out_date_dialog: false,
          check_in_date: this.$moment().format("YYYY-MM-DD"),
          check_out_date: this.$moment().format("YYYY-MM-DD"),
          parameter_default_value: 0,
          parameter_name: "",
          facility_name: "",
          parameter_id: 0,
          supplement_price: [],
        },
        credit_note_activities: {
          activity: {},
          number_of_adults: 0,
          max_adults: 0,
          number_of_children: 0,
          max_children: 0,
          discount: 0,
          number_of_extra_adults: 0,
          max_extra_adults: 0,
          number_of_extra_children: 0,
          max_extra_children: 0,
          activity_date_dialog: false,
          activity_date: this.$moment().format("YYYY-MM-DD"),
        },
        catering_type_dialog: false,
        catering_type: {
          booking_id: "",
          catering_type: "",
          old_catering_type: "",
          facility_booking_id: "",
          save_count: 3,
          session_id: "",
        },
        search: "",
        foc: false,
        statuses: ["Mark as FOC", "Mark As Paid", "Mark as Confirmed"],
        message: "",
        currency_id: "",
        emails: {
          email: "",
        },
        credit_note: {
          amount: "",
          reason: "",
        },
        note: {
          booking_id: this.$route.params.booking_id,
          note: "",
          show_on_invoice: false,
        },
        columns: [
          { text: "Timestamp", name: "created_at" },
          { text: "Activity", name: "booking_activity_description" },
          { text: "User", name: "session_user" },
        ],
        transactionColumns: [
          { text: "Transacted At" },
          { text: "Payment Method" },
          { text: "Transaction Code" },
          { text: "Original Amount" },
          { text: "Transaction Total" },
          { text: "Transaction Balance" },
          { text: "Transacted By" },
        ],
        creditNoteColumns: [
          { text: "Credit Note Ref" },
          { text: "Amount" },
          { text: "Reason" },
          { text: "Issued By" },
          { text: "Issued At" },
        ],
        inspectionNoteColumns: [
          { text: "Inspection Note" },
          { text: "Status" },
          { text: "Inspected By" },
          { text: "Inspected At" },
        ],
        residencyProofColumns: [
          { text: "Name" },
          { text: "Residency" },
          { text: "Point of Proof" },
          { text: "Document ID" },
          { text: "Verified" },
          { text: "Verified By" },
          { text: "Created At" },
          { text: "Image" },
        ],
        booking_details_facilities_arr: this.$store.state.BookingStore.booking.facility_bookings,
        currentDay: this.$moment().format("YYYY-MM-DD"),
        checkInDate: this.$moment().format("YYYY-MM-DD"),
        verificationDialog: false,
        verifying: false,
        verificationData: {
          id_number: "",
          serial_number: ""
        },
        verificationResult: null,
        imageGalleryDialog: false,
        galleryImages: [],
        imageDialog: false,
        viewingImage: null,
        viewingImageDialog: false,
        currentGalleryIndex: 0,
        verificationApiUrl: "https://bookings.olpejetaconservancy.org"
      };
    },
    computed: {
      singleBooking() {
        return this.$store.state.BookingStore.booking;
      },
      singleBookingCheckIn() {
        return this.$store.state.BookingStore.booking_checkin;
      },
      totalCost: function () {
        return null;
      },
      is_past_booking: function () {
        if (this.singleBooking) {
          this.checkInDate = this.$moment(this.singleBooking.booking_check_in_date).format("YYYY-MM-DD");
          let yesterday = this.$moment(this.today).subtract(1, "days").format("YYYY-MM-DD");
          if (this.checkInDate < yesterday || (this.checkInDate == yesterday && this.singleBooking.status == "No-Show") || (this.checkInDate == yesterday && this.singleBooking.status === "Cancelled")) {
            return "True";
          } else {
            return "False";
          }
        }
      },
      hasUnverifiedCitizen: function () {
        if (!this.singleBooking || !this.singleBooking.guests) return false;
        for (var i = 0; i < this.singleBooking.guests.length; i++) {
          var guest = this.singleBooking.guests[i];
          if (guest.payment_person && guest.payment_person.toLowerCase().includes('citizen') && guest.payment_person.toLowerCase().includes('adult') && guest.verified !== true) {
            return true;
          }
        }
        return false;
      },
      allGuests() {
        const booking = this.singleBooking;
        if (!booking || !booking.guests) return [];
        let proofs = [];
        if (this.singleBookingCheckIn && this.singleBookingCheckIn.residency_proof) {
          proofs = this.singleBookingCheckIn.residency_proof;
        } else if (booking.residency_proof) {
          proofs = booking.residency_proof;
        }
        return booking.guests
          .filter(g => g.payment_guests > 0)
          .map(g => {
            const verifiedCount = proofs.filter(p =>
              p.payment_person === g.payment_person && p.verified === true
            ).length;
            const proofObj = proofs.find(p => p.payment_person === g.payment_person && p.verified === true);
            const lastProof = proofObj ? proofObj.document_id : null;
            return {
              ...g,
              verifiedCount,
              lastProof
            };
          });
      },
      verifiableGuestTypes() {
        return this.allGuests.filter(g => {
          const lower = (g.payment_person || '').toLowerCase();
          if (lower.includes('citizen child')) return false;
          if (lower.includes('citizen student')) return false;
          if (lower.includes('infant')) return false;
          if (lower.includes('non resident child')) return false;
          if (lower.includes('non resident adult')) return false;
          if (lower.includes('non resident student')) return false;
          if (lower.includes('ea resident child')) return false;
          return true;
        });
      },
      exemptGuestTypes() {
        return this.allGuests.filter(g => {
          const lower = (g.payment_person || '').toLowerCase();
          if (lower.includes('citizen child')) return true;
          if (lower.includes('citizen student')) return true;
          if (lower.includes('infant')) return true;
          if (lower.includes('non resident child')) return true;
          if (lower.includes('non resident adult')) return true;
          if (lower.includes('non resident student')) return true;
          if (lower.includes('ea resident child')) return true;
          return false;
        });
      },
      totalVerificationsNeeded() {
        return this.verifiableGuestTypes.reduce((sum, g) => sum + g.payment_guests, 0);
      },
      totalVerified() {
        return this.verifiableGuestTypes.reduce((sum, g) => sum + (g.verifiedCount || 0), 0);
      },
      allVerifiableGuestsVerified() {
        return this.totalVerified === this.totalVerificationsNeeded;
      }
    },
    methods: {
      showToast(message, type = 'info') {
        if (this.$toast && this.$toast[type]) {
          this.$toast[type](message);
        } else {
          if (type === 'error' || type === 'warning') {
            alert(message);
          }
        }
      },
      getResidencyProofs() {
        let proofs = [];
        if (this.singleBookingCheckIn && this.singleBookingCheckIn.residency_proof) {
          proofs = this.singleBookingCheckIn.residency_proof;
        } else if (this.singleBooking && this.singleBooking.residency_proof) {
          proofs = this.singleBooking.residency_proof;
        }
        return proofs;
      },
      getGuestVerifiedImages(guest) {
        const proofs = this.getResidencyProofs();
        return proofs.filter(p =>
          p.payment_person === guest.payment_person &&
          p.verified === true &&
          p.has_image === true
        );
      },
      viewGuestVerificationImages(guest) {
        const images = this.getGuestVerifiedImages(guest);
        if (images.length === 0) {
          this.$toast.warning('No images available for this verification');
          return;
        }
        this.galleryImages = images;
        this.currentGalleryIndex = 0;
        this.viewingImage = images[0];
        this.imageGalleryDialog = true;
        this.logImageView("Guest document thumbnail image viewed");
      },
      viewVerificationImage(item) {
        if (!item.image_data) {
          this.$toast.warning('No image available for this verification');
          return;
        }
        this.viewingImage = item;
        this.imageDialog = true;
        this.logImageView("image viewed");
      },
      getImageSrc(item) {
        if (!item || !item.image_data) return '';
        const mimeType = item.image_mime_type || 'image/jpeg';
        return `data:${mimeType};base64,${item.image_data}`;
      },
      downloadImage(item) {
        if (!item || !item.image_data) return;
        const link = document.createElement('a');
        link.href = this.getImageSrc(item);
        const fileName = `verification_${item.residency_proof_public_id || 'image'}.jpg`;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      },
      downloadAllImages() {
        this.galleryImages.forEach((image, index) => {
          setTimeout(() => {
            this.downloadImage(image);
          }, index * 500);
        });
      },
      openFullImage(item) {
        this.viewingImage = item;
        this.currentGalleryIndex = this.galleryImages.indexOf(item);
        this.viewingImageDialog = true;
        this.logImageView("Guest document image viewed in fullscreen");
      },
      logImageView(description) {
        this.$store.dispatch("log_image_view", {
          booking_id: this.$route.params.booking_id,
          session_id: localStorage.getItem("lkjhgfdsa"),
          description: description
        });
      },
      viewNextImage() {
        if (this.currentGalleryIndex < this.galleryImages.length - 1) {
          this.currentGalleryIndex++;
          this.viewingImage = this.galleryImages[this.currentGalleryIndex];
        }
      },
      viewPreviousImage() {
        if (this.currentGalleryIndex > 0) {
          this.currentGalleryIndex--;
          this.viewingImage = this.galleryImages[this.currentGalleryIndex];
        }
      },
      openJavaApplication() {
        this.$store.state.LoadingStore.loading = true;
        var data = {
          booking_id: this.$route.params.booking_id,
          currency_id: this.currency_id,
          session_id: localStorage.getItem("lkjhgfdsa"),
        };
        this.$store.dispatch("request_receipt", data);
      },
      openVerificationDialog(guest) {
        this.currentGuest = guest;
        this.verificationDialog = true;
        this.verificationData.id_number = "";
        this.verificationData.serial_number = "";
        this.verificationResult = null;
      },
      closeCreditDialog() {
        this.credit_dialog = false;
        let a = [];
        localStorage.setItem("credit_note_guests", JSON.stringify(a));
        localStorage.setItem("credit_note_vehicles", JSON.stringify(a));
        localStorage.setItem("credit_note_accommodations", JSON.stringify(a));
        localStorage.setItem("credit_note_external_accommodations", JSON.stringify(a));
        localStorage.setItem("credit_note_activities", JSON.stringify(a));
      },
      populateGuestData(data) {
        this.credit_note_guests.guest = data;
        this.credit_note_guests.number_of_guests = data.payment_guests;
        this.credit_note_guests.max_guests = data.payment_guests;
        this.credit_note_guests.check_in_date = this.$moment(
          this.singleBooking.booking_check_in_date
        ).format("YYYY-MM-DD");
        this.credit_note_guests.check_out_date = this.$moment(
          this.singleBooking.booking_check_out_date
        ).format("YYYY-MM-DD");
      },
      addGuestToCreditNote() {
        let creditNoteGuests = JSON.parse(
          localStorage.getItem("credit_note_guests")
        );
        creditNoteGuests.push(this.credit_note_guests);
        localStorage.setItem(
          "credit_note_guests",
          JSON.stringify(creditNoteGuests)
        );
        this.credit_note_details.guests = JSON.parse(
          localStorage.getItem("credit_note_guests")
        );
        this.credit_note_guests.guest = {};
        this.credit_note_guests.number_of_guests = 0;
        this.credit_note_guests.max_guests = 0;
        this.credit_note_guests.guest_discount = 0;
        this.credit_note_guests.check_in_date =
          this.$moment().format("YYYY-MM-DD");
        this.credit_note_guests.check_out_date =
          this.$moment().format("YYYY-MM-DD");
      },
      creditNoteGuestTotal(guest) {
        var total = 0;
        var discount = guest.guest.payment_person_discount;
        var cost = guest.guest.payment_person_amount;
        var guests = guest.number_of_guests;
        var days = this.getDays(guest.check_in_date, guest.check_out_date);
        total = (1 - Number(discount / 100)) * Number(cost) * days * guests;
        return total;
      },
      removeGuestFromCreditNote(key) {
        let creditNoteGuests = JSON.parse(
          localStorage.getItem("credit_note_guests")
        );
        creditNoteGuests.splice(key, 1);
        localStorage.setItem(
          "credit_note_guests",
          JSON.stringify(creditNoteGuests)
        );
        this.credit_note_details.guests = JSON.parse(
          localStorage.getItem("credit_note_guests")
        );
      },
      populateVehicleData(data) {
        this.credit_note_vehicles.vehicle = data;
        this.credit_note_vehicles.number_of_vehicles = data.vehicles;
        this.credit_note_vehicles.max_vehicles = data.vehicles;
        this.credit_note_vehicles.check_in_date = this.$moment(
          this.singleBooking.booking_check_in_date
        ).format("YYYY-MM-DD");
        this.credit_note_vehicles.check_out_date = this.$moment(
          this.singleBooking.booking_check_out_date
        ).format("YYYY-MM-DD");
      },
      addVehicleToCreditNote() {
        let creditNoteVehicles = JSON.parse(
          localStorage.getItem("credit_note_vehicles")
        );
        creditNoteVehicles.push(this.credit_note_vehicles);
        localStorage.setItem(
          "credit_note_vehicles",
          JSON.stringify(creditNoteVehicles)
        );
        this.credit_note_details.vehicles = JSON.parse(
          localStorage.getItem("credit_note_vehicles")
        );
        this.credit_note_vehicles.vehicle = {};
        this.credit_note_vehicles.number_of_vehicles = 0;
        this.credit_note_vehicles.max_vehicles = 0;
        this.credit_note_vehicles.vehicle_discount = 0;
        this.credit_note_vehicles.check_in_date =
          this.$moment().format("YYYY-MM-DD");
        this.credit_note_vehicles.check_out_date =
          this.$moment().format("YYYY-MM-DD");
      },
      creditNoteVehicleTotal(vehicle) {
        var total = 0;
        var discount = vehicle.vehicle.discount;
        var cost = vehicle.vehicle.vehicle_charge_category_cost;
        var vehicles = vehicle.number_of_vehicles;
        var days = this.getDays(vehicle.check_in_date, vehicle.check_out_date);
        total = (1 - Number(discount / 100)) * Number(cost) * days * vehicles;
        return total;
      },
      removeVehicleFromCreditNote(key) {
        let creditNoteVehicles = JSON.parse(
          localStorage.getItem("credit_note_vehicles")
        );
        creditNoteVehicles.splice(key, 1);
        localStorage.setItem(
          "credit_note_vehicles",
          JSON.stringify(creditNoteVehicles)
        );
        this.credit_note_details.vehicles = JSON.parse(
          localStorage.getItem("credit_note_vehicles")
        );
      },
      populateAccommodationData(data) {
        this.credit_note_accommodations.accommodation = data;
        this.credit_note_accommodations.number_of_adults =
          data.facility_booking_adults;
        this.credit_note_accommodations.max_adults = data.facility_booking_adults;
        this.credit_note_accommodations.number_of_children =
          data.facility_booking_children;
        this.credit_note_accommodations.max_children =
          data.facility_booking_children;
        this.credit_note_accommodations.number_of_extra_adults =
          data.facility_booking_extra_adults;
        this.credit_note_accommodations.max_extra_adults =
          data.facility_booking_extra_adults;
        this.credit_note_accommodations.number_of_extra_children =
          data.facility_booking_extra_children;
        this.credit_note_accommodations.max_extra_children =
          data.facility_booking_extra_children;
        this.credit_note_accommodations.check_in_date = this.$moment(
          data.facility_booking_check_in_date
        ).format("YYYY-MM-DD");
        this.credit_note_accommodations.check_out_date = this.$moment(
          data.facility_booking_check_out_date
        ).format("YYYY-MM-DD");
      },
      addAccommodationToCreditNote() {
        let creditNoteAccommodations = JSON.parse(
          localStorage.getItem("credit_note_accommodations")
        );
        creditNoteAccommodations.push(this.credit_note_accommodations);
        localStorage.setItem(
          "credit_note_accommodations",
          JSON.stringify(creditNoteAccommodations)
        );
        this.credit_note_details.accommodations = JSON.parse(
          localStorage.getItem("credit_note_accommodations")
        );
        this.credit_note_accommodations.accommodation = {};
        this.credit_note_accommodations.number_of_adults = 0;
        this.credit_note_accommodations.max_adults = 0;
        this.credit_note_accommodations.number_of_children = 0;
        this.credit_note_accommodations.max_children = 0;
        this.credit_note_accommodations.number_of_extra_adults = 0;
        this.credit_note_accommodations.max_extra_adults = 0;
        this.credit_note_accommodations.number_of_extra_children = 0;
        this.credit_note_accommodations.max_extra_children = 0;
        this.credit_note_accommodations.check_in_date = this.$moment(
          this.singleBooking.booking_check_in_date
        ).format("YYYY-MM-DD");
        this.credit_note_accommodations.check_out_date = this.$moment(
          this.singleBooking.booking_check_out_date
        ).format("YYYY-MM-DD");
      },
      creditNoteAccommodationTotal(accommodation) {
        var total = 0;
        var facilityType = accommodation.accommodation.facility_type;
        var accommodationType = accommodation.accommodation.accomodation_type;
        var discount =
          accommodation.accommodation.facility_booking_discount_percentage;
        var adultCost =
          accommodation.accommodation.facility_booking_cost_per_adult;
        var childCost =
          accommodation.accommodation.facility_booking_cost_per_child;
        var adults = accommodation.number_of_adults;
        var children = accommodation.number_of_children;
        var extraAdults = accommodation.number_of_extra_adults;
        var extraChildren = accommodation.number_of_extra_children;
        var days = this.getDays(
          accommodation.check_in_date,
          accommodation.check_out_date
        );
        if (facilityType === "Accomodation") {
          if (accommodationType === "Pelican") {
            var adultTotal =
              (1 - Number(discount / 100)) * Number(adultCost) * days * adults;
            var childTotal =
              (1 - Number(discount / 100)) * Number(childCost) * days * children;
            var extraAdultTotal =
              (1 - Number(discount / 100)) *
              Number(adultCost) *
              days *
              extraAdults;
            var extraChildTotal =
              (1 - Number(discount / 100)) *
              Number(childCost) *
              days *
              extraChildren;
            total = adultTotal + childTotal + extraAdultTotal + extraChildTotal;
          } else if (accommodationType === "Stables") {
            var adultTotal =
              (1 - Number(discount / 100)) * Number(adultCost) * days * adults;
            var childTotal =
              (1 - Number(discount / 100)) * Number(childCost) * days * children;
            var extraAdultTotal =
              (1 - Number(discount / 100)) *
              Number(adultCost) *
              days *
              extraAdults;
            var extraChildTotal =
              (1 - Number(discount / 100)) *
              Number(childCost) *
              days *
              extraChildren;
            total = adultTotal + childTotal + extraAdultTotal + extraChildTotal;
          }
        } else if (facilityType === "Camping Sites") {
        }
        return total;
      },
      removeAccommodationFromCreditNote(key) {
        let creditNoteAccommodations = JSON.parse(
          localStorage.getItem("credit_note_accommodations")
        );
        creditNoteAccommodations.splice(key, 1);
        localStorage.setItem(
          "credit_note_accommodations",
          JSON.stringify(creditNoteAccommodations)
        );
        this.credit_note_details.accommodations = JSON.parse(
          localStorage.getItem("credit_note_accommodations")
        );
      },
      populateExternalAccommodationData(data) {
        this.credit_note_external_accommodation.facility_booking_public_id =
          data.facility_booking_public_id;
        this.credit_note_external_accommodation.parameter_default_value =
          data.pricing_parameter.parameter_default_value;
        this.credit_note_external_accommodation.parameter_name =
          data.pricing_parameter.parameter_name;
        this.credit_note_external_accommodation.parameter_id =
          data.pricing_parameter.parameter_id;
        this.credit_note_external_accommodation.discount =
          data.facility_booking_discount_percentage;
        this.credit_note_external_accommodation.facility_name =
          data.facility_name;
        this.credit_note_external_accommodation.supplement_price =
          data.pricing_parameter.supplement_prices;
        this.credit_note_external_accommodation.check_in_date = this.$moment(
          data.facility_booking_check_in_date
        ).format("YYYY-MM-DD");
        this.credit_note_external_accommodation.check_out_date = this.$moment(
          data.facility_booking_check_out_date
        ).format("YYYY-MM-DD");
      },
      addExternalAccommodationToCreditNote() {
        let creditNoteExternalAccommodations = JSON.parse(
          localStorage.getItem("credit_note_external_accommodations")
        );
        creditNoteExternalAccommodations.push(
          this.credit_note_external_accommodation
        );
        localStorage.setItem(
          "credit_note_external_accommodations",
          JSON.stringify(creditNoteExternalAccommodations)
        );
        this.credit_note_details.external_accommodations = JSON.parse(
          localStorage.getItem("credit_note_external_accommodations")
        );
        this.credit_note_external_accommodation.facility_booking_public_id = "";
        this.credit_note_external_accommodation.facility_name = "";
        this.credit_note_external_accommodation.parameter_name = "";
        this.credit_note_external_accommodation.parameter_id = "";
        this.credit_note_external_accommodation.parameter_default_value = 0;
        this.credit_note_external_accommodation.discount = 0;
        this.credit_note_external_accommodation.check_in_date = this.$moment(
          this.singleBooking.booking_check_in_date
        ).format("YYYY-MM-DD");
        this.credit_note_external_accommodation.check_out_date = this.$moment(
          this.singleBooking.booking_check_out_date
        ).format("YYYY-MM-DD");
      },
      removeExternalAccommodationFromCreditNote(key) {
        let creditNoteExternalAccommodations = JSON.parse(
          localStorage.getItem("credit_note_external_accommodations")
        );
        creditNoteExternalAccommodations.splice(key, 1);
        localStorage.setItem(
          "credit_note_external_accommodations",
          JSON.stringify(creditNoteExternalAccommodations)
        );
        this.credit_note_details.external_accommodations = JSON.parse(
          localStorage.getItem("credit_note_external_accommodations")
        );
      },
      populateActivityData(data) {
        this.credit_note_activities.activity = data;
        this.credit_note_activities.number_of_adults =
          data.inventory_booking_adults;
        this.credit_note_activities.max_adults = data.inventory_booking_adults;
        this.credit_note_activities.number_of_children =
          data.inventory_booking_children;
        this.credit_note_activities.max_children =
          data.inventory_booking_children;
        this.credit_note_activities.number_of_extra_adults =
          data.inventory_booking_extra_adults;
        this.credit_note_activities.max_extra_adults =
          data.inventory_booking_extra_adults;
        this.credit_note_activities.number_of_extra_children =
          data.inventory_booking_extra_children;
        this.credit_note_activities.max_extra_children =
          data.inventory_booking_extra_children;
        this.credit_note_activities.activity_date = this.$moment(
          data.inventory_booking_date
        ).format("YYYY-MM-DD");
      },
      addActivityToCreditNote() {
        let creditNoteActivities = JSON.parse(
          localStorage.getItem("credit_note_activities")
        );
        creditNoteActivities.push(this.credit_note_activities);
        localStorage.setItem(
          "credit_note_activities",
          JSON.stringify(creditNoteActivities)
        );
        this.credit_note_details.activities = JSON.parse(
          localStorage.getItem("credit_note_activities")
        );
        this.credit_note_activities.activity = {};
        this.credit_note_activities.number_of_adults = 0;
        this.credit_note_activities.max_adults = 0;
        this.credit_note_activities.number_of_children = 0;
        this.credit_note_activities.max_children = 0;
        this.credit_note_activities.number_of_extra_adults = 0;
        this.credit_note_activities.max_extra_adults = 0;
        this.credit_note_activities.number_of_extra_children = 0;
        this.credit_note_activities.max_extra_children = 0;
        this.credit_note_activities.activity_date =
          this.$moment().format("YYYY-MM-DD");
      },
      removeActivityFromCreditNote(key) {
        let creditNoteActivities = JSON.parse(
          localStorage.getItem("credit_note_activities")
        );
        creditNoteActivities.splice(key, 1);
        localStorage.setItem(
          "credit_note_activities",
          JSON.stringify(creditNoteActivities)
        );
        this.credit_note_details.activities = JSON.parse(
          localStorage.getItem("credit_note_activities")
        );
      },
      postCreditNote() {
        this.credit_note_details.booking_id = this.$route.params.booking_id;
        this.credit_note_details.currency = this.singleBooking.currency_id;
        this.$store.dispatch("issue_credit_note", this.credit_note_details);
        this.credit_dialog = false;
        let a = [];
        localStorage.setItem("credit_note_guests", JSON.stringify(a));
        localStorage.setItem("credit_note_vehicles", JSON.stringify(a));
        localStorage.setItem("credit_note_accommodations", JSON.stringify(a));
        localStorage.setItem(
          "credit_note_external_accommodations",
          JSON.stringify(a)
        );
        localStorage.setItem("credit_note_activities", JSON.stringify(a));
      },
      printCreditNote() {
        this.print_credit_note.booking_id = this.$route.params.booking_id;
        this.print_credit_note.session_id = localStorage.getItem("lkjhgfdsa");
        this.$store.dispatch("print_credit_note", this.print_credit_note);
        this.print_credit_note_dialog = false;
      },
      issueCreditNote() {
        var data = {
          booking_id: this.$route.params.booking_id,
          currency: this.singleBooking.currency_id,
          amount: this.credit_note.amount,
          reason: this.credit_note.reason,
          session_id: localStorage.getItem("lkjhgfdsa"),
        };
        this.$store.dispatch("issue_credit_note", data);
        this.credit_dialog = false;
        this.credit_note.amount = "";
        this.credit_note.reason = "";
      },
      emailAddresses() {
        return this.singleBooking.booking_details.email_address;
      },
      changeStatus(param) {
        this.$store.state.LoadingStore.loading = true;
        var data = {
          booking_id: this.$route.params.booking_id,
          session_id: localStorage.getItem("lkjhgfdsa"),
        };
        if (param === "Mark as FOC") {
          this.$store.dispatch("mark_as_foc", data);
        } else if (param === "Mark As Paid") {
          this.$store.dispatch("mark_as_paid", data);
        } else if (param === "Mark as Confirmed") {
          this.$store.dispatch("mark_as_confirmed", data);
        }
      },
      openEditCateringTypeDialog(facility) {
        this.catering_type_dialog = true;
        this.catering_type.facility_booking_id = facility.facility_booking_public_id;
        this.catering_type.catering_type = facility.facility_catering_type;
        this.catering_type.old_catering_type = facility.facility_catering_type;
      },
      updateCateringType() {
        this.catering_type.booking_id = this.$route.params.booking_id;
        this.catering_type.session_id = localStorage.getItem("lkjhgfdsa");
        this.catering_type.save_count = 3;
        this.catering_type_dialog = false;
        this.$store.dispatch("edit_catering_type", this.catering_type);
      },
      openEditActivityDateDialog(activity) {
        this.edit_activity_date_dialog = true;
        this.edit_activity_date_data.activity_name = activity.inventory_name;
        this.edit_activity_date_data.inventory_booking_public_id = activity.inventory_booking_public_id;
        this.edit_activity_date_data.activity_date = this.$moment(activity.inventory_booking_date).format("YYYY-MM-DD");
        this.edit_activity_date_data.old_activity_date = this.$moment(activity.inventory_booking_date).format("YYYY-MM-DD");
      },
      updateActivityDate() {
        this.edit_activity_date_dialog = false;
        this.edit_activity_date_data.delete_count = 3;
        if (this.edit_activity_date_data.activity_date !== this.edit_activity_date_data.old_activity_date) {
          var data = {
            inventory_booking_id: this.edit_activity_date_data.inventory_booking_public_id,
            date: this.edit_activity_date_data.activity_date,
            old_date: this.edit_activity_date_data.old_activity_date,
            inventory_name: this.edit_activity_date_data.activity_name,
            booking_id: this.$route.params.booking_id,
            session_id: localStorage.getItem("lkjhgfdsa"),
          };
          this.$store.dispatch("edit_activity_date", data);
        }
      },
      openExternalFacilityDetail(facility) {
        this.$router.push({ name: 'ExternalFacilityDetail', params: { id: facility.facility_booking_public_id } });
      },
      openEditAccommodationDatesDialog(facility) {
        this.edit_accommodation_dates_dialog = true;
        this.edit_accommodation_dates_data.accommodation_name = facility.facility_name;
        this.edit_accommodation_dates_data.accommodation_booking_public_id = facility.facility_booking_public_id;
        this.edit_accommodation_dates_data.check_in_date = this.$moment(facility.facility_booking_check_in_date).format("YYYY-MM-DD");
        this.edit_accommodation_dates_data.check_out_date = this.$moment(facility.facility_booking_check_out_date).format("YYYY-MM-DD");
        this.edit_accommodation_dates_data.old_check_in_date = this.$moment(facility.facility_booking_check_in_date).format("YYYY-MM-DD");
        this.edit_accommodation_dates_data.old_check_out_date = this.$moment(facility.facility_booking_check_out_date).format("YYYY-MM-DD");
      },
      updateAccommodationDates() {
        this.edit_accommodation_dates_dialog = false;
        this.edit_accommodation_dates_data.delete_count = 3;
        var data = {
          facility_booking_id: this.edit_accommodation_dates_data.accommodation_booking_public_id,
          check_in_date: this.edit_accommodation_dates_data.check_in_date,
          old_check_in_date: this.edit_accommodation_dates_data.old_check_in_date,
          check_out_date: this.edit_accommodation_dates_data.check_out_date,
          old_check_out_date: this.edit_accommodation_dates_data.old_check_out_date,
          facility_name: this.edit_accommodation_dates_data.accommodation_name,
          booking_id: this.$route.params.booking_id,
          session_id: localStorage.getItem("lkjhgfdsa"),
        };
        this.$store.dispatch("edit_accommodation_dates", data);
      },
      totalGuestsVehicles() {
        let total = 0;
        if (this.singleBooking.guests.length > 0) {
          for (let x = 0; x < this.singleBooking.guests.length; x++) {
            if (this.singleBooking.guests[x].payment_guests > 0) {
              total = total + this.singleBooking.guests[x].payment_guests;
            }
          }
        }
        if (this.singleBooking.vehicles.length > 0) {
          for (let x = 0; x < this.singleBooking.vehicles.length; x++) {
            if (this.singleBooking.vehicles[x].vehicles > 0) {
              total = total + this.singleBooking.vehicles[x].vehicles;
            }
          }
        }
        return total;
      },
      getDays(startDate, endDate) {
        if (startDate === endDate) {
          return 1;
        } else {
          var b = this.$moment(endDate);
          var a = this.$moment(startDate);
          return b.diff(a, "days");
        }
      },
      postNote() {
        this.$store.dispatch("post_note", this.note);
        this.note.note = "";
        this.note.show_on_invoice = false;
      },
      printReceipt() {
        this.$store.dispatch("print_receipt", {
          booking_id: this.$route.params.booking_id,
          currency_id: this.currency_id
        });
        this.dialog11 = false;
      },
      populateEmailValue() {
        this.emails.email = this.singleBooking.booking_details.email_address;
      },
      populateCurrencyValue() {
        this.currency_id = this.singleBooking.currency_id;
      },
      sendMail() {
        this.$store.dispatch("send_mail", {
          booking_id: this.$route.params.booking_id,
          email: this.emails.email,
          currency_id: this.currency_id,
          message: this.message,
          session_id: localStorage.getItem("lkjhgfdsa")
        });
        this.dialog = false;
        this.message = "";
      },
      openBooking: function () {
        this.$store.state.FacilityStore.chosenFacilities = [];
        this.$store.state.FacilityStore.chosenExternalFacility = [];
        this.$store.state.FacilityStore.multi_camping_arrivals = [];
        this.$store.state.BookingStore.camping_multi_browser_facilities = [];
        this.$store.state.ActivityStore.chosenInventory = [];
        this.$store.commit("REMOVE_CHOSEN_FACILITIES");
        this.$store.commit("REMOVE_CHOSEN_ACTIVITIES");
        this.$store.commit("REMOVE_MULTI_ARRIVALS");
        this.$store.commit("REMOVE_CHOSEN_EXTERNAL_FACILITIES");
        this.$store.commit("ALREADY_BOOKED_ACTIVITIES", JSON.stringify([]));
        this.$store.commit("ALREADY_BOOKED_FACILITIES", JSON.stringify([]));
        this.$store.commit(
          "ALREADY_BOOKED_EXTERNAL_FACILITIES",
          JSON.stringify([])
        );
        this.$store.commit("ALREADY_PROOFED_RESIDENCY", JSON.stringify([]));
        this.$store.commit(
          "ALREADY_BOOKED_ACTIVITIES",
          this.singleBooking.inventory_bookings
        );
        this.$store.commit(
          "ALREADY_BOOKED_FACILITIES",
          this.singleBooking.facility_bookings
        );
        this.$store.commit(
          "ALREADY_BOOKED_EXTERNAL_FACILITIES",
          JSON.stringify([])
        );
        this.$store.commit(
          "ALREADY_PROOFED_RESIDENCY",
          this.singleBooking.residency_proof
        );
        localStorage.setItem("old_activity_data", JSON.stringify([]));
        localStorage.setItem("old_facility_data", JSON.stringify([]));
        localStorage.setItem("old_external_facility_data", JSON.stringify([]));
        localStorage.setItem("old_residency_proofs", JSON.stringify([]));
        this.$store.commit(
          "ALREADY_BOOKED_EXTERNAL_FACILITIES",
          this.singleBooking.external_facility_bookings
        );
        localStorage.setItem(
          "old_facility_data",
          JSON.stringify(this.singleBooking.facility_bookings)
        );
        localStorage.setItem(
          "old_external_facility_data",
          JSON.stringify(this.singleBooking.external_facility_bookings)
        );
        localStorage.setItem(
          "old_activity_data",
          JSON.stringify(this.singleBooking.inventory_bookings)
        );
        localStorage.setItem(
          "old_residency_proofs",
          JSON.stringify(this.singleBooking.residency_proof)
        );
        this.$router.push({
          name: "EditBooking",
          params: {
            booking_id: this.$route.params.booking_id,
          },
        });
      },
      openPostponeBooking: function () {
        localStorage.setItem("postponed_inventory_data", JSON.stringify([]));
        localStorage.setItem("postponed_facility_data", JSON.stringify([]));
        localStorage.setItem(
          "postponed_external_facility_data",
          JSON.stringify([])
        );
        this.$store.commit("ALREADY_BOOKED_ACTIVITIES", JSON.stringify([]));
        this.$store.commit(
          "ALREADY_BOOKED_ACTIVITIES",
          this.singleBooking.inventory_bookings
        );
        this.$store.commit("ALREADY_BOOKED_FACILITIES", JSON.stringify([]));
        this.$store.commit(
          "ALREADY_BOOKED_FACILITIES",
          this.singleBooking.facility_bookings
        );
        this.$store.commit(
          "ALREADY_BOOKED_EXTERNAL_FACILITIES",
          JSON.stringify([])
        );
        this.$store.commit(
          "ALREADY_BOOKED_EXTERNAL_FACILITIES",
          this.singleBooking.external_facility_bookings
        );
        localStorage.setItem("old_facility_data", JSON.stringify([]));
        localStorage.setItem(
          "old_facility_data",
          JSON.stringify(this.singleBooking.facility_bookings)
        );
        localStorage.setItem("old_external_facility_data", JSON.stringify([]));
        localStorage.setItem(
          "old_external_facility_data",
          JSON.stringify(this.singleBooking.external_facility_bookings)
        );
        localStorage.setItem("old_activity_data", JSON.stringify([]));
        localStorage.setItem(
          "old_activity_data",
          JSON.stringify(this.singleBooking.inventory_bookings)
        );
        this.$router.push({
          name: "PostponeBooking",
          params: {
            booking_id: this.$route.params.booking_id,
          },
        });
      },
      openPayment: function () {
        this.$router.push({
          name: "GatePayment",
          params: {
            booking_id: this.$route.params.booking_id,
          },
        });
      },
      openGuestPayment: function () {
        this.$router.push({
          name: "GuestPaymentInformation",
          params: {
            booking_id: this.$route.params.booking_id,
          },
        });
      },
      sendReminder: function () {
        this.$store.dispatch(
          "send_email_payment_reminder",
          this.$route.params.booking_id
        );
      },
      checkIn: function () {
        this.$router.push({
          name: "Checkin",
          params: {
            booking_id: this.$route.params.booking_id,
          },
        });
      },
      checkOut() {
        this.$store.dispatch("check_out", {
          booking_id: this.$route.params.booking_id,
          session_id: localStorage.getItem("lkjhgfdsa")
        });
      },
      cancelBooking: function () {
        this.canceling = false;
        var data = {
          booking_id: this.$route.params.booking_id,
          session_id: localStorage.getItem("lkjhgfdsa"),
        };
        this.$store.dispatch("delete_booking", data);
      },
      deferPayment() {
        this.$store.dispatch("defer_payment", {
          booking_id: this.$route.params.booking_id,
          session_id: localStorage.getItem("lkjhgfdsa")
        });
      },
      reinstateBooking() {
        this.$store.dispatch("reinstate_booking", {
          booking_id: this.$route.params.booking_id,
          session_id: localStorage.getItem("lkjhgfdsa")
        });
      },
      isGuestVerifiable(guest) {
        const lower = (guest.payment_person || '').toLowerCase();
        if (lower.includes('citizen child')) return false;
        if (lower.includes('citizen student')) return false;
        if (lower.includes('infant')) return false;
        if (lower.includes('non resident child')) return false;
        if (lower.includes('non resident adult')) return false;
        if (lower.includes('non resident student')) return false;
        if (lower.includes('ea resident child')) return false;
        return true;
      },
      getVerifiedCountForGuest(guest) {
        let proofs = [];
        if (this.singleBookingCheckIn && this.singleBookingCheckIn.residency_proof) {
          proofs = this.singleBookingCheckIn.residency_proof;
        } else if (this.singleBooking.residency_proof) {
          proofs = this.singleBooking.residency_proof;
        }
        return proofs.filter(p =>
          p.payment_person === guest.payment_person && p.verified === true
        ).length;
      },
      getGuestDocTypeLabel(guest) {
        const type = this.getGuestDocumentType(guest);
        const labels = {
          'passport': 'Passport',
          'kenyan_id': 'Kenyan ID',
          'work_permit': 'Work Permit',
          'exempt': 'Exempt'
        };
        return labels[type] || 'Passport';
      },
      getGuestDocTypeColor(guest) {
        const type = this.getGuestDocumentType(guest);
        const colors = {
          'passport': 'success',
          'kenyan_id': 'primary',
          'work_permit': 'info',
          'exempt': 'grey'
        };
        return colors[type] || 'success';
      },
      getGuestDocTypeIcon(guest) {
        const type = this.getGuestDocumentType(guest);
        const icons = {
          'passport': 'passport',
          'kenyan_id': 'card-account-details',
          'work_permit': 'card-account-details',
          'exempt': 'check-circle'
        };
        return icons[type] || 'document';
      },
      getGuestDocumentType(guest) {
        const name = guest.payment_person || '';
        const lower = name.toLowerCase();
        if (lower.includes('citizen child')) return 'exempt';
        if (lower.includes('citizen student')) return 'exempt';
        if (lower.includes('infant')) return 'exempt';
        if (lower.includes('non resident child')) return 'exempt';
        if (lower.includes('non resident adult')) return 'exempt';
        if (lower.includes('non resident student')) return 'exempt';
        if (lower.includes('ea resident child')) return 'exempt';
        if (lower.includes('citizen')) return 'kenyan_id';
        if (lower.includes('ea resident')) return 'work_permit';
        if (lower.includes('non resident')) return 'passport';
        if (lower.includes('school driver')) return 'kenyan_id';
        if (lower.includes('resident')) return 'work_permit';
        return 'passport';
      }
    },
    beforeCreate() {
      this.$store.dispatch("get_booking", this.$route.params.booking_id);
      this.$store.dispatch("get_all_facility_pricing_types");
      localStorage.setItem("credit_note_guests", JSON.stringify([]));
      localStorage.setItem("credit_note_vehicles", JSON.stringify([]));
      localStorage.setItem("credit_note_accommodations", JSON.stringify([]));
      localStorage.setItem("credit_note_external_accommodations", JSON.stringify([]));
      localStorage.setItem("credit_note_activities", JSON.stringify([]));
    },
    created() {
      this.$store.dispatch("get_booking", this.$route.params.booking_id);
      this.$store.dispatch("get_booking_checkin", this.$route.params.booking_id);
    },
  };
</script>

<style scoped>
  .booking-summary-container {
    zoom: 0.9;
    --scale-factor: 0.9;
    font-size: calc(1em * var(--scale-factor));
  }

  .booking-summary-header {
    background: linear-gradient(145deg, #2d5016, #4a7c2b);
    border-radius: 16px;
    padding: 24px 32px;
    position: relative;
    overflow: hidden;
    box-shadow: 0 8px 32px rgba(46, 80, 20, 0.25);
    margin-bottom: 8px;
  }

    .booking-summary-header::before {
      content: '';
      position: absolute;
      top: -50%;
      right: -20%;
      width: 300px;
      height: 300px;
      background: rgba(255, 255, 255, 0.06);
      border-radius: 50%;
      pointer-events: none;
    }

    .booking-summary-header::after {
      content: '';
      position: absolute;
      bottom: -60%;
      left: -10%;
      width: 200px;
      height: 200px;
      background: rgba(255, 255, 255, 0.04);
      border-radius: 50%;
      pointer-events: none;
    }

  .summary-title {
    color: #ffffff !important;
    position: relative;
    z-index: 2;
    font-weight: 700;
    letter-spacing: 1.5px;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
  }

  .booking-ref-in-header {
    font-size: 0.6em;
    font-weight: 400;
    color: rgba(255, 255, 255, 0.75);
    margin-left: 12px;
    letter-spacing: 0.5px;
  }

  .summary-header-decoration {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #f5b342, #f1c40f, #f5b342);
    z-index: 2;
  }

  .summary-cards-wrapper {
    margin-bottom: 16px;
  }

  .beautiful-summary-card {
    border-radius: 16px;
    background: linear-gradient(145deg, #ffffff, #f8faf5);
    border: 1px solid rgba(116, 165, 51, 0.15);
    transition: all 0.3s cubic-bezier(0.22, 1, 0.36, 1);
    position: relative;
    overflow: hidden;
    min-height: 110px;
  }

    .beautiful-summary-card:hover {
      transform: translateY(-4px);
      box-shadow: 0 12px 40px rgba(116, 165, 51, 0.15);
      border-color: rgba(116, 165, 51, 0.30);
    }

    .beautiful-summary-card::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 4px;
      background: linear-gradient(90deg, #74a533, #a8d06a);
    }

    .beautiful-summary-card .v-container {
      padding-right: 40px !important;
      overflow-wrap: break-word;
      word-wrap: break-word;
    }

  .summary-item {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    flex-wrap: wrap;
    width: 100%;
  }

  .summary-item-title {
    font-size: 0.85rem;
    font-weight: 700;
    color: #2d3e1a;
    letter-spacing: 0.3px;
    white-space: normal;
    word-break: break-word;
    flex: 1 1 auto;
    min-width: 0;
    margin-right: 8px;
  }

    .summary-item-title .clickable {
      cursor: pointer;
      color: #2d5016;
      text-decoration: none;
      border-bottom: 1px dashed rgba(116, 165, 51, 0.4);
    }

      .summary-item-title .clickable:hover {
        color: #4a7c2b;
        border-bottom-color: #74a533;
      }

  .facility-catering {
    font-weight: 400;
    font-size: 0.7rem;
    color: #6b8f4a;
    white-space: normal;
    word-break: break-word;
  }

  .count-badge {
    padding: 2px 14px;
    border-radius: 30px;
    font-weight: 700;
    font-size: 0.8rem;
    flex-shrink: 0;
    margin-left: auto;
  }

  .gradient-badge {
    background: linear-gradient(135deg, #74a533, #8bc34a);
    color: #ffffff;
    box-shadow: 0 2px 8px rgba(116, 165, 51, 0.30);
    text-shadow: 0 1px 2px rgba(0,0,0,0.1);
    transition: transform 0.2s, box-shadow 0.2s;
  }

    .gradient-badge:hover {
      transform: scale(1.05);
      box-shadow: 0 4px 12px rgba(116, 165, 51, 0.4);
    }

  .summary-item-sub {
    font-size: 0.75rem;
    color: #5a6d4a;
    margin-top: 2px;
    white-space: normal;
    word-break: break-word;
  }

  .summary-item-cost {
    font-size: 0.7rem;
    color: #6b7a5a;
    margin-top: 2px;
    white-space: normal;
    word-break: break-word;
  }

  .summary-item-total {
    font-size: 0.8rem;
    font-weight: 700;
    color: #2d5016;
    margin-top: 2px;
    white-space: normal;
    word-break: break-word;
  }

  .summary-item-nights-guests,
  .summary-item-nights-vehicles {
    font-size: 0.7rem;
    color: #4a6a3a;
    margin-top: 2px;
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }

  .summary-item-icon {
    position: absolute;
    bottom: 8px;
    right: 12px;
    opacity: 0.20;
  }

  .pickup-info {
    display: inline-block;
    margin-left: 6px;
    font-size: 0.7rem;
    color: #6b7a5a;
    white-space: normal;
    word-break: break-word;
  }

  .proof-text {
    font-size: 0.65rem;
    color: #7a8a6a;
    white-space: normal;
    word-break: break-word;
  }

  .verification-status {
    margin-top: 4px;
    padding-top: 4px;
    border-top: 1px solid rgba(116, 165, 51, 0.08);
  }

  .verification-chip {
    font-size: 0.65rem;
    height: 20px;
    line-height: 20px;
  }

  .beautiful-info-card {
    border-radius: 16px;
    overflow: hidden;
    background: #ffffff;
    border: 1px solid rgba(116, 165, 51, 0.12);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
    transition: all 0.3s ease;
  }

    .beautiful-info-card:hover {
      box-shadow: 0 8px 30px rgba(116, 165, 51, 0.08);
      border-color: rgba(116, 165, 51, 0.20);
    }

  .info-card-header {
    background: linear-gradient(145deg, #2d5016, #3d6b22);
    padding: 12px 20px;
    color: #ffffff;
    font-weight: 600;
    font-size: 0.8rem;
    letter-spacing: 1.2px;
    display: flex;
    align-items: center;
    gap: 8px;
  }

    .info-card-header .v-icon {
      font-size: 18px;
    }

  .info-card-body {
    padding: 16px 20px;
    background: #fafcf8;
  }

  .ref-code {
    font-size: 1.4rem;
    font-weight: 700;
    color: #2d5016;
    letter-spacing: 1px;
    margin-bottom: 8px;
    padding: 4px 0;
    border-bottom: 2px solid rgba(116, 165, 51, 0.15);
    word-break: break-word;
  }

  .info-row {
    font-size: 0.8rem;
    padding: 2px 0;
    color: #3d4a2e;
    white-space: normal;
    word-break: break-word;
  }

  .info-label {
    font-weight: 600;
    color: #2d5016;
    margin-right: 4px;
  }

  .sub-details {
    margin-top: 12px;
    padding-top: 10px;
    border-top: 1px solid rgba(116, 165, 51, 0.12);
  }

  .sub-details-title {
    font-weight: 600;
    font-size: 0.75rem;
    letter-spacing: 1px;
    color: #4a7c2b;
    margin-bottom: 4px;
  }

  .details-list {
    list-style-type: none;
    padding-left: 0;
    margin: 0;
  }

  .detail-item {
    margin-bottom: 6px;
    font-size: 0.85rem;
  }

  .monospace-text {
    font-family: 'Courier New', monospace;
    font-size: 0.85em;
  }

  .action-footer {
    background: rgba(255, 255, 255, 0.92);
    backdrop-filter: blur(8px);
    border-top: 1px solid rgba(116, 165, 51, 0.15);
    z-index: 10;
    padding: 8px 0;
    box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.04);
  }

    .action-footer .v-btn {
      min-width: 80px;
      margin: 4px 4px;
      border-radius: 30px;
      font-weight: 500;
      letter-spacing: 0.5px;
      transition: all 0.2s ease;
    }

      .action-footer .v-btn:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
      }

  .clickable {
    cursor: pointer;
  }

  .status-select {
    font-size: 0.9em;
  }

  .summary-card {
    width: 100%;
  }

  .card-header {
    padding: 12px 16px;
  }

  .card-body {
    padding: 16px;
  }

  .text-big {
    font-size: 1.5em;
  }

  .font-light {
    font-weight: 300;
  }

  .booking-ref {
    font-size: 1.2em;
    word-break: break-all;
  }

  .booking-meta {
    font-size: 0.9em;
    margin-bottom: 4px;
  }

  .client-detail {
    font-size: 0.9em;
    margin-bottom: 4px;
  }

  .compact-summary-card {
    border: 1px solid #e0e0e0;
    border-radius: 8px;
  }

  .summary-item {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    flex-wrap: wrap;
  }

  .count-badge {
    padding: 4px 12px;
    border-radius: 20px;
    font-weight: bold;
  }

  .grey-badge {
    background-color: #e0e0e0;
    color: #333;
  }

  .date-range {
    font-size: 0.85em;
    margin-top: 4px;
  }

  .cost-details {
    font-size: 0.9em;
  }

  .pickup-info {
    display: block;
    margin-top: 4px;
  }

  .status-chips {
    margin-bottom: 16px;
  }

  .note-card {
    border: 1px solid #e0e0e0;
    border-radius: 8px;
  }

  .note-tag {
    font-size: 0.8em;
  }

    .note-tag.internal {
      background-color: #9e9e9e;
      color: white;
    }

  .note-content {
    margin: 8px 0;
  }

  .note-meta {
    font-size: 0.8em;
    color: #757575;
  }

  .note-form {
    margin-bottom: 24px;
  }

  .section-title {
    margin-top: 24px;
    margin-bottom: 16px;
  }

  .section-subtitle {
    margin-top: 16px;
    margin-bottom: 8px;
  }

  .summary-panel {
    margin-bottom: 8px;
  }

  .panel-header {
    width: 100%;
  }

  .panel-subheader {
    margin-top: 8px;
    margin-bottom: 8px;
    color: #5d4037;
  }

  .details-list {
    list-style-type: none;
    padding-left: 0;
  }

  .detail-item {
    margin-bottom: 8px;
    font-size: 0.9em;
  }

  .monospace-text {
    font-family: 'Courier New', monospace;
    font-size: 0.85em;
  }

  .divider {
    margin: 8px 0;
    border-color: #e0e0e0;
  }

  .tab-header {
    font-weight: bold;
    letter-spacing: 1px;
  }

  .action-btn {
    letter-spacing: 1px;
  }

  .print-badge {
    padding: 8px 16px;
    border-radius: 4px;
  }

  .school-details,
  .corporate-details,
  .partner-details {
    margin-top: 16px;
  }

  .booking-ref-in-header {
    font-size: 0.7em;
    font-weight: normal;
    color: #555;
    margin-left: 8px;
  }

  .verification-status {
    margin-top: 4px;
    padding-top: 4px;
    border-top: 1px solid rgba(116, 165, 51, 0.08);
  }

  .verification-chip {
    font-size: 0.65rem;
    height: 20px;
    line-height: 20px;
  }

  .image-gallery {
    max-height: 70vh;
    overflow-y: auto;
  }

  .gallery-card {
    border-radius: 8px;
    overflow: hidden;
    cursor: pointer;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
    height: 100%;
  }

    .gallery-card:hover {
      transform: translateY(-4px);
      box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
    }

  .gallery-thumbnail-wrapper {
    position: relative;
    width: 100%;
    padding-top: 75%;
    background: #f5f5f5;
    overflow: hidden;
  }

  .gallery-thumbnail {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .gallery-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.3);
    display: flex;
    justify-content: center;
    align-items: center;
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  .gallery-card:hover .gallery-overlay {
    opacity: 1;
  }

  .gallery-meta {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
  }

  .image-container {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .image-wrapper {
    width: 100%;
    max-height: 500px;
    overflow: hidden;
    border-radius: 8px;
    border: 1px solid #e0e0e0;
    background: #f5f5f5;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .verification-image {
    max-width: 100%;
    max-height: 500px;
    object-fit: contain;
    cursor: pointer;
    transition: transform 0.2s;
  }

    .verification-image:hover {
      transform: scale(1.02);
    }

  .full-image-container {
    width: 100vw;
    height: calc(100vh - 64px);
    display: flex;
    justify-content: center;
    align-items: center;
    background: #000;
    padding: 20px;
  }

  .full-verification-image {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }

  .image-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    justify-content: center;
  }

  .image-actions {
    display: flex;
    gap: 12px;
    justify-content: center;
  }

  @media (max-width: 600px) {
    .verification-image {
      max-height: 300px;
    }

    .image-wrapper {
      max-height: 300px;
    }

    .full-image-container {
      padding: 10px;
    }

    .gallery-thumbnail-wrapper {
      padding-top: 100%;
    }

    .image-gallery {
      max-height: 60vh;
    }
  }

  @media (max-width: 960px) {
    .beautiful-summary-card {
      min-height: 90px;
    }

    .booking-summary-header {
      padding: 16px 20px;
    }

    .summary-title {
      font-size: 1.2rem;
    }

    .info-card-body {
      padding: 12px 16px;
    }

    .ref-code {
      font-size: 1.1rem;
    }
  }

  @media (max-width: 600px) {
    .beautiful-summary-card {
      min-height: 80px;
    }

    .booking-summary-header {
      padding: 12px 16px;
      border-radius: 12px;
    }

    .summary-title {
      font-size: 1rem;
    }

    .booking-ref-in-header {
      font-size: 0.5em;
      margin-left: 6px;
    }

    .info-card-body {
      padding: 10px 12px;
    }

    .ref-code {
      font-size: 0.95rem;
    }

    .info-row {
      font-size: 0.72rem;
    }
  }

  .large-font-card .summary-item-title {
    font-size: 1.1rem;
  }

  .large-font-card .summary-item-sub {
    font-size: 0.95rem;
  }

  .large-font-card .summary-item-cost {
    font-size: 0.9rem;
  }

  .large-font-card .summary-item-total {
    font-size: 1rem;
  }

  .large-font-card .summary-item-nights-guests,
  .large-font-card .summary-item-nights-vehicles {
    font-size: 0.9rem;
  }

  .large-font-card .count-badge.large-badge {
    font-size: 1rem;
    padding: 4px 18px;
  }

  .large-font-card .facility-catering {
    font-size: 0.85rem;
  }

  .large-font-card .proof-text {
    font-size: 0.8rem;
  }

  .donation-large .info-card-header {
    font-size: 1rem;
  }

  .donation-large .info-card-body {
    font-size: 1rem;
  }

  .donation-large .detail-item {
    font-size: 1rem;
  }
</style>
